import React, { useState, useRef, useEffect, useCallback } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform, TextInput, Image, ScrollView, Animated, Easing, Alert, Dimensions, Clipboard } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import * as ImagePicker from 'expo-image-picker';
import { Ionicons } from '@expo/vector-icons';
import NavigationHeader from '../components/NavigationHeader';
import { BlurView } from 'expo-blur';
import { GlassBackgroundGradient, GradientOverlay } from '../components/Gradients';
import { useAuth } from '../context/AuthContext';
import { useNavigation } from '../context/NavigationContext';
import { useLoading } from '../context/LoadingContext';
import { useBackgroundQuery } from '../context/BackgroundQueryContext';
import { useTicker, AVAILABLE_TICKER_OPTIONS, DEFAULT_TICKER_LEAGUES, SPORT_CATEGORIES, getLeaguesBySport, TICKER_NAME_FORMATS } from '../context/TickerContext';
import { db } from '../firebaseConfig';
import { collection, onSnapshot, orderBy, query as fsQuery, where, getDocs, deleteDoc, doc } from 'firebase/firestore';
import * as DocumentPicker from 'expo-document-picker';
import { getAuth, sendPasswordResetEmail } from 'firebase/auth';

// Reset Password Section Component with proper feedback
const ResetPasswordSection = ({ email }) => {
    const [resetStatus, setResetStatus] = useState('idle'); // 'idle' | 'sending' | 'sent' | 'error'
    const [resetError, setResetError] = useState('');
    
    const handleResetPassword = async () => {
        if (!email) {
            setResetStatus('error');
            setResetError('Please enter your email address first');
            return;
        }
        
        setResetStatus('sending');
        setResetError('');
        
        try {
            const auth = getAuth();
            await sendPasswordResetEmail(auth, email);
            setResetStatus('sent');
            
            // Reset to idle after 5 seconds
            setTimeout(() => {
                setResetStatus('idle');
            }, 5000);
        } catch (error) {
            console.error('Reset password error:', error);
            setResetStatus('error');
            setResetError(error.message || 'Failed to send reset email');
            
            // Reset to idle after 5 seconds
            setTimeout(() => {
                setResetStatus('idle');
                setResetError('');
            }, 5000);
        }
    };
    
    return (
        <View style={{ marginTop: 16 }}>
            <Text style={{ 
                color: 'rgba(255,255,255,0.8)', 
                fontSize: 14, 
                fontWeight: '600',
                marginBottom: 8 
            }}>
                Password
            </Text>
            <TouchableOpacity
                disabled={resetStatus === 'sending'}
                style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 10,
                    padding: 12,
                    backgroundColor: resetStatus === 'sent' 
                        ? 'rgba(74, 222, 128, 0.15)' 
                        : resetStatus === 'error' 
                            ? 'rgba(248, 113, 113, 0.15)'
                            : 'rgba(255,255,255,0.05)',
                    borderRadius: 8,
                    borderWidth: 1,
                    borderColor: resetStatus === 'sent' 
                        ? 'rgba(74, 222, 128, 0.4)' 
                        : resetStatus === 'error' 
                            ? 'rgba(248, 113, 113, 0.4)'
                            : 'rgba(255,255,255,0.1)',
                    opacity: resetStatus === 'sending' ? 0.7 : 1,
                }}
                onPress={handleResetPassword}
            >
                <Ionicons 
                    name={
                        resetStatus === 'sent' ? 'checkmark-circle' : 
                        resetStatus === 'error' ? 'alert-circle' : 
                        resetStatus === 'sending' ? 'time' : 'key-outline'
                    } 
                    size={18} 
                    color={
                        resetStatus === 'sent' ? '#4ade80' : 
                        resetStatus === 'error' ? '#f87171' : 
                        'rgba(255,255,255,0.7)'
                    } 
                />
                <Text style={{ 
                    color: resetStatus === 'sent' ? '#4ade80' : 
                           resetStatus === 'error' ? '#f87171' : 
                           'rgba(255,255,255,0.8)', 
                    fontSize: 14, 
                    fontWeight: '500',
                    flex: 1
                }}>
                    {resetStatus === 'sending' ? 'Sending Reset Email...' :
                     resetStatus === 'sent' ? '✓ Reset Email Sent!' :
                     resetStatus === 'error' ? 'Failed - Try Again' :
                     'Reset Password'}
                </Text>
                {resetStatus === 'idle' && (
                    <Ionicons name="chevron-forward" size={16} color="rgba(255,255,255,0.5)" />
                )}
            </TouchableOpacity>
            <Text style={{ 
                color: resetStatus === 'sent' ? '#4ade80' : 
                       resetStatus === 'error' ? '#f87171' : 
                       'rgba(255,255,255,0.5)', 
                fontSize: 12, 
                marginTop: 6 
            }}>
                {resetStatus === 'sent' 
                    ? 'Check your inbox for the password reset link'
                    : resetStatus === 'error' 
                        ? resetError
                        : 'Send a password reset link to your email address'
                }
            </Text>
        </View>
    );
};

const ProfilePage = () => {
    const {
        user,
        logout,
        userProfile,
        profileLoading,
        saveUserProfile,
        uploadProfilePhoto,
        removeProfilePhoto
    } = useAuth();
    const { navigateTo, isAdmin } = useNavigation();
    const { startLogout } = useLoading();
    const { isTickerEnabled, toggleTicker, totalHeaderHeight, tickerLeagues, updateTickerLeagues, tickerNameFormat, updateTickerNameFormat, showTickerSGPIndicator, updateShowTickerSGPIndicator, tickerSGPOnly, updateTickerSGPOnly } = useTicker();
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState('settings'); // 'settings' | 'betslips' | 'savedQueries' | 'watchedFixtures'
    const [betSlips, setBetSlips] = useState([]);
    const [betSlipsLoading, setBetSlipsLoading] = useState(true);
    const [savedQueries, setSavedQueries] = useState([]);
    const [savedQueriesLoading, setSavedQueriesLoading] = useState(true);
    const [watchedFixtures, setWatchedFixtures] = useState([]);
    const [watchedFixturesLoading, setWatchedFixturesLoading] = useState(true);
    
    // Query usage tracking
    const [queryUsage, setQueryUsage] = useState({ queriesUsed: 0, queryLimit: 10, planType: 'FREE' });
    const [queryUsageLoading, setQueryUsageLoading] = useState(true);

    // Animation values
    const pageOpacity = useRef(new Animated.Value(0)).current;
    const pageTranslateY = useRef(new Animated.Value(30)).current;
    const [isExiting, setIsExiting] = useState(false);
    
    // Section animations
    const profileSectionOpacity = useRef(new Animated.Value(0)).current;
    const accountSectionOpacity = useRef(new Animated.Value(0)).current;
    const preferencesSectionOpacity = useRef(new Animated.Value(0)).current;
    const actionButtonsOpacity = useRef(new Animated.Value(0)).current;

    // User profile data
    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [profilePhoto, setProfilePhoto] = useState('');
    const [subscriptionPlan, setSubscriptionPlan] = useState(null);

    // Math settings
    const [kellyCriterion, setKellyCriterion] = useState('0.25');
    const [legsPerPage, setLegsPerPage] = useState('10');
    const [minBooksStraights, setMinBooksStraights] = useState('3');
    const [minBooksSGP, setMinBooksSGP] = useState('4');
    const [unitSize, setUnitSize] = useState('$10');

    // Toggle settings
    const [showAllMath, setShowAllMath] = useState(false);
    const [defaultBetMode, setDefaultBetMode] = useState('infer');
    const [autoSwitchMode, setAutoSwitchMode] = useState(true);
    const [defaultSortMethod, setDefaultSortMethod] = useState('ev'); // 'ev' or 'odds'
    const [backgroundQueryMode, setBackgroundQueryMode] = useState(false); // Background queries by default

    // Background query context
    const { queryHistory, historyLoading, deleteFromHistory, clearHistory, loadQueryHistory } = useBackgroundQuery();

    // Sportsbook filters (tag-like selection)
    const [selectedSportsbooks, setSelectedSportsbooks] = useState([]);
    const [showAllFilters, setShowAllFilters] = useState(false);
    const [sportsbookLimits, setSportsbookLimits] = useState({});
    const [enabledSportsbookLimits, setEnabledSportsbookLimits] = useState({});

    // Sync state with userProfile from context
    // Listen for profile photo changes from AuthContext
    useEffect(() => {
        const photoUrl = userProfile?.profilePhoto || userProfile?.photoURL || user?.photoURL || user?.profilePhoto || '';
        if (photoUrl) {
            setProfilePhoto(photoUrl);
        }
    }, [userProfile?.profilePhoto, userProfile?.photoURL, user?.photoURL, user?.profilePhoto]);

    useEffect(() => {
        if (userProfile) {
            setUsername(userProfile.username || '');
            setEmail(userProfile.email || '');
            setPhone(userProfile.phoneNumber || '');
            setProfilePhoto(userProfile.profilePhoto || userProfile.photoURL || user?.photoURL || '');

            // Extract subscription plan from userProfile
            if (userProfile.subscription) {
                const subscription = userProfile.subscription;
                if (subscription.planType) {
                    setSubscriptionPlan(subscription.planType);
                } else if (subscription.subscriptionStatus === 'active') {
                    // If no planType but status is active, we can try to determine from other data
                    setSubscriptionPlan('ACTIVE');
                } else {
                    setSubscriptionPlan('FREE');
                }
            } else {
                setSubscriptionPlan('FREE');
            }

            if (userProfile.preferences) {
                const prefs = userProfile.preferences;
                setKellyCriterion(prefs.kellyCriterion?.toString() || '0.25');
                setLegsPerPage(prefs.legsPerPage?.toString() || '5');
                setMinBooksStraights(prefs.minBooksStraights?.toString() || '3');
                setMinBooksSGP(prefs.minBooksSGP?.toString() || '4');
                setUnitSize(`$${prefs.unitSize || 10}`);
                setShowAllMath(prefs.showAllMath || false);
                setDefaultBetMode(prefs.defaultBetMode || 'infer');
                setAutoSwitchMode(prefs.autoSwitchMode !== undefined ? prefs.autoSwitchMode : true);
                setDefaultSortMethod(prefs.defaultSortMethod || 'ev');
                setBackgroundQueryMode(prefs.backgroundQueryMode || false);
                setSelectedSportsbooks(prefs.selectedSportsbooks || []);
                setSportsbookLimits(prefs.sportsbookLimits || {});
                setEnabledSportsbookLimits(prefs.enabledSportsbookLimits || {});
            }
        }
    }, [userProfile]);

    // Page load animation
    useEffect(() => {
        // Main page animation
        Animated.parallel([
            Animated.timing(pageOpacity, {
                toValue: 1,
                duration: 1200,
                easing: Easing.inOut(Easing.cubic),
                useNativeDriver: true,
            }),
            Animated.timing(pageTranslateY, {
                toValue: 0,
                duration: 1000,
                easing: Easing.out(Easing.back(1.1)),
                useNativeDriver: true,
            })
        ]).start();

        // Staggered section animations
        const sectionAnimations = [
            { ref: profileSectionOpacity, delay: 200 },
            { ref: accountSectionOpacity, delay: 400 },
            { ref: preferencesSectionOpacity, delay: 600 },
            { ref: actionButtonsOpacity, delay: 800 }
        ];

        sectionAnimations.forEach(({ ref, delay }) => {
            Animated.timing(ref, {
                toValue: 1,
                duration: 600,
                delay: delay,
                easing: Easing.out(Easing.cubic),
                useNativeDriver: true,
            }).start();
        });
    }, []);

    // Register exit animation with navigation context
    useEffect(() => {
        if (Platform.OS === 'web') {
            window.profileExitAnimation = exitAnimation;
        }
        
        return () => {
            if (Platform.OS === 'web') {
                delete window.profileExitAnimation;
            }
        };
    }, []);

    // Load betslips
    useEffect(() => {
        if (!user?.uid) return;
        try {
            const ref = collection(db, 'users', user.uid, 'betslips');
            const q = fsQuery(ref, orderBy('createdAt', 'desc'));
            const unsub = onSnapshot(q, (snap) => {
                const list = [];
                snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
                setBetSlips(list);
                setBetSlipsLoading(false);
            });
            return () => unsub();
        } catch {
            setBetSlipsLoading(false);
        }
    }, [user?.uid]);

    // Load saved queries
    useEffect(() => {
        const fetchSavedQueries = async () => {
            if (!user?.uid) {
                setSavedQueriesLoading(false);
                return;
            }
            
            try {
                const q = fsQuery(
                    collection(db, 'saved_queries'),
                    where('uid', '==', user.uid),
                    orderBy('createdAt', 'desc')
                );
                const snapshot = await getDocs(q);
                const queries = snapshot.docs.map(d => ({
                    id: d.id,
                    ...d.data()
                }));
                setSavedQueries(queries);
                setSavedQueriesLoading(false);
            } catch (error) {
                console.error('[ProfilePage] Error fetching saved queries:', error);
                setSavedQueriesLoading(false);
            }
        };
        
        fetchSavedQueries();
    }, [user?.uid]);

    // Load watched fixtures
    useEffect(() => {
        const fetchWatchedFixtures = async () => {
            if (!user?.uid) {
                setWatchedFixturesLoading(false);
                return;
            }
            
            try {
                const q = fsQuery(
                    collection(db, 'query_watchers'),
                    where('uid', '==', user.uid),
                    where('status', '==', 'active'),
                    orderBy('createdAt', 'desc')
                );
                const snapshot = await getDocs(q);
                const fixtures = snapshot.docs.map(d => ({
                    id: d.id,
                    ...d.data()
                }));
                setWatchedFixtures(fixtures);
                setWatchedFixturesLoading(false);
            } catch (error) {
                console.error('[ProfilePage] Error fetching watched fixtures:', error);
                setWatchedFixturesLoading(false);
            }
        };
        
        fetchWatchedFixtures();
    }, [user?.uid]);

    // Load user data on component mount
    useEffect(() => {
        if (user) {
            setUsername(user.username || '');
            setEmail(user.email || '');
            setPhone(user.phoneNumber || '');
            setProfilePhoto(user.profilePhoto || user.photoURL || '');

            // Load preferences
            if (user.preferences) {
                const prefs = user.preferences;
                setKellyCriterion(prefs.kellyCriterion || '0.25');
                setLegsPerPage(prefs.legsPerPage || '5');
                setMinBooksStraights(prefs.minBooksStraights || '3');
                setMinBooksSGP(prefs.minBooksSGP || '4');
                setUnitSize(prefs.unitSize || '$10');
                setShowAllMath(prefs.showAllMath || false);
                setDefaultBetMode(prefs.defaultBetMode || 'infer');
                setAutoSwitchMode(prefs.autoSwitchMode !== undefined ? prefs.autoSwitchMode : true);
                setSelectedSportsbooks(prefs.selectedSportsbooks || []);
                setSportsbookLimits(prefs.sportsbookLimits || {});
                setEnabledSportsbookLimits(prefs.enabledSportsbookLimits || {});
            }
        }
    }, [user]);

    // Load query usage on mount
    useEffect(() => {
        const loadQueryUsage = async () => {
            if (!user?.uid) return;
            
            setQueryUsageLoading(true);
            try {
                const isLocalhost = Platform.OS === 'web' && 
                    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
                const base = isLocalhost
                    ? 'http://127.0.0.1:5001/bet-optimized-fe922/us-central1'
                    : 'https://us-central1-bet-optimized-fe922.cloudfunctions.net';
                
                const response = await fetch(`${base}/getQueryUsage`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ userId: user.uid })
                });
                
                if (response.ok) {
                    const data = await response.json();
                    setQueryUsage(data);
                }
            } catch (error) {
                console.error('[ProfilePage] Error loading query usage:', error);
            } finally {
                setQueryUsageLoading(false);
            }
        };
        
        loadQueryUsage();
    }, [user?.uid]);

    // Animated Input Component with internal focus handling
    const AnimatedInput = useCallback(({ value, onChangeText, placeholder, style }) => {
        const focusT = useRef(new Animated.Value(0)).current;

        const onFocus = () => {
            Animated.timing(focusT, {
                toValue: 1,
                duration: 1500,
                easing: Easing.bezier(0.22, 1, 0.36, 1),
                useNativeDriver: true
            }).start();
        };

        const onBlur = () => {
            Animated.timing(focusT, {
                toValue: 0,
                duration: 1500,
                easing: Easing.bezier(0.22, 1, 0.36, 1),
                useNativeDriver: true
            }).start();
        };

        return (
            <View style={[styles.customInputContainer, style]}>
                <GradientOverlay opacity={focusT} borderRadius={4} />
                <GlassBackgroundGradient borderRadius={4} />
                <BlurView intensity={60} tint="dark" style={styles.customInputBlur}>
                    <TextInput
                        style={styles.customInputText}
                        value={value}
                        onChangeText={onChangeText}
                        placeholder={placeholder}
                        placeholderTextColor="#B5B5B5"
                        onFocus={onFocus}
                        onBlur={onBlur}
                    />
                </BlurView>
            </View>
        );
    }, []);

    // Simple Input Component for preferences (no gradients)
    const SimpleInput = useCallback(({ value, onChangeText, placeholder, style }) => (
        <View style={[styles.settingValue, style]}>
            <LinearGradient
                colors={['rgba(255, 255, 255, 0.05)', 'rgba(255, 255, 255, 0.10)']}
                start={{ x: 0, y: 0 }}
                end={{ x: 0, y: 1 }}
                style={styles.settingValueGradient}
            />
            <TextInput
                style={styles.simpleInputText}
                value={value}
                onChangeText={onChangeText}
                placeholder={placeholder}
            />
        </View>
    ), []);

    const [allSportsbooks, setAllSportsbooks] = useState([]);
    const [showAllBooks, setShowAllBooks] = useState(false);
    useEffect(() => {
        (async () => {
            try {
                const Constants = (await import('expo-constants')).default;
                const Platform = (await import('react-native')).Platform;
                
                // Determine functions URL: use emulator on localhost, production otherwise
                const isLocalhost = Platform.OS === 'web' && 
                    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
                
                const base = isLocalhost
                    ? 'http://127.0.0.1:5001/bet-optimized-fe922/us-central1' // Emulator
                    : (
                        (typeof process !== 'undefined' && process?.env?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
                        || (Constants?.expoConfig?.extra?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
                        || (Constants?.expoConfig?.extra?.FUNCTIONS_BASE_URL)
                        || (Constants?.manifest?.extra?.FUNCTIONS_BASE_URL)
                        || 'https://us-central1-bet-optimized-fe922.cloudfunctions.net' // Production
                    );
                const res = await fetch(`${base}/opticOddsProxy?path=${encodeURIComponent('/sportsbooks?limit=200')}`);
                const txt = await res.text();
                let arr = [];
                try { const body = JSON.parse(txt); arr = Array.isArray(body?.data) ? body.data : (Array.isArray(body) ? body : (Array.isArray(body?.items) ? body.items : [])); } catch {}
                const names = arr.map(b => b?.name || b).filter(Boolean);
                
                // Define the 35 active sportsbooks that are queried in the cache (from oddsRefresh.js)
                const activeSportsbooks = [
                    // Tier 1
                    'DraftKings', 'FanDuel', 'BetMGM', 'Caesars', 'ESPNBet',
                    // Tier 2
                    'BetRivers', 'Fanatics', 'BetOnline', 'Bovada', 'HardRock',
                    // Tier 3
                    'Superbook', 'MyBookie', 'GoldenNugget', 'WilliamHill', 'BallyBet',
                    // Tier 4
                    'Pinnacle', 'Betfred', 'BetUS', 'Borgata', 'BetPARX',
                    // Tier 5
                    'Circa Vegas', 'Novig', 'Underdog Sportsbook', 'Sleeper',
                    // Tier 6
                    'Prophet X', 'Kalshi', 'BookMaker', 'Resorts World Bet', 'Tipico',
                    // Tier 7
                    'Rebet', 'Fliff', 'Betr', 'WynnBET', 'STN Sports'
                ];
                
                // Separate into active (available in cache) and coming soon
                const active = names.filter(name => activeSportsbooks.includes(name));
                const comingSoon = names.filter(name => !activeSportsbooks.includes(name));
                
                // Sort active books by tier order
                const sorted = active.sort((a, b) => {
                    const aIdx = activeSportsbooks.indexOf(a);
                    const bIdx = activeSportsbooks.indexOf(b);
                    if (aIdx >= 0 && bIdx >= 0) return aIdx - bIdx;
                    return a.localeCompare(b);
                });
                
                // Append coming soon books at the end (alphabetically sorted)
                const comingSoonSorted = comingSoon.sort((a, b) => a.localeCompare(b));
                const allBooks = [...sorted, ...comingSoonSorted];
                
                setAllSportsbooks(allBooks);
            } catch {}
        })();
    }, []);

    // Sportsbook selection handlers - UNIFIED: both filter and limit
    const toggleSportsbook = (sportsbook) => {
        setSelectedSportsbooks(prev =>
            prev.includes(sportsbook)
                ? prev.filter(s => s !== sportsbook)
                : [...prev, sportsbook]
        );
    };

    const updateSportsbookLimit = (sportsbook, limit) => {
        setSportsbookLimits(prev => ({
            ...prev,
            [sportsbook]: limit
        }));
    };

    const toggleSportsbookLimit = (sportsbook) => {
        setEnabledSportsbookLimits(prev => ({
            ...prev,
            [sportsbook]: !prev[sportsbook]
        }));
    };
    
    // Clear all sportsbook filters
    const clearAllSportsbookFilters = () => {
        setSelectedSportsbooks([]);
    };

    // Profile photo upload function
    const handleProfilePhotoUpload = async () => {
        if (!user?.uid) {
            Alert.alert('Error', 'Please log in again');
            return;
        }

        try {
            const result = await DocumentPicker.getDocumentAsync({ type: 'image/*' });
            if (!result.canceled && result.assets && result.assets.length > 0) {
                const file = result.assets[0];
                const fileUri = file.uri;
                const response = await fetch(fileUri);
                const fileBlob = await response.blob();

                const fileExt = file.name.split('.').pop();
                const fileName = `${user.uid}.${fileExt}`;

                const uploadResult = await uploadProfilePhoto(fileBlob, fileName);
                
                if (uploadResult.success) {
                    // Photo URL should be updated via AuthContext state
                    // But we also set local state to ensure immediate UI update
                    setProfilePhoto(uploadResult.photoURL);
                } else {
                    console.error('[ProfilePage] Upload failed:', uploadResult.error);
                    // Error toast already dispatched by uploadProfilePhoto
                }
            }
        } catch (error) {
            console.error('Error uploading profile photo:', error);
            Alert.alert('Error', 'Failed to update profile photo. Please try again.');
        }
    };

    // Profile photo remove function
    const handleProfilePhotoRemove = async () => {
        if (!user?.uid) {
            Alert.alert('Error', 'Please log in again');
            return;
        }
        const result = await removeProfilePhoto();
        if (result.success) {
            setProfilePhoto('');
        }
    };

    // Image picker function (fallback for mobile)
    const pickImage = async () => {
        try {
            // Request permission
            const permissionResult = await ImagePicker.requestMediaLibraryPermissionsAsync();

            if (permissionResult.granted === false) {
                Alert.alert("Permission Required", "Permission to access camera roll is required!");
                return;
            }

            // Launch image picker
            const result = await ImagePicker.launchImageLibraryAsync({
                mediaTypes: ImagePicker.MediaTypeOptions.Images,
                allowsEditing: true,
                aspect: [1, 1],
                quality: 0.8,
            });

            if (!result.canceled) {
                // For mobile, we'll use the image picker result directly
                setProfilePhoto(result.assets[0].uri);
            }
        } catch (error) {
            Alert.alert("Error", "Failed to pick image. Please try again.");
            console.error('Image picker error:', error);
        }
    };

    // Save function
    const handleSave = async () => {
        if (!user?.uid) {
            Alert.alert('Error', 'Please log in again');
            return;
        }

        setSaving(true);

        const updatedUserData = {
            username,
            email,
            phoneNumber: phone,
            profilePhoto,
            preferences: {
                kellyCriterion: parseFloat(kellyCriterion),
                legsPerPage: parseInt(legsPerPage),
                minBooksStraights: parseInt(minBooksStraights),
                minBooksSGP: parseInt(minBooksSGP),
                unitSize: parseInt(String(unitSize).replace('$', '')),
                showAllMath,
                defaultBetMode,
                autoSwitchMode,
                defaultSortMethod,
                backgroundQueryMode,
                selectedSportsbooks,
                sportsbookLimits,
                enabledSportsbookLimits,
                tickerLeagues: tickerLeagues || DEFAULT_TICKER_LEAGUES,
            }
        };

        try {
            const result = await saveUserProfile(updatedUserData);
            if (result.success) {
                Alert.alert('Success', 'Profile updated successfully!');
            } else {
                Alert.alert('Error', result.error || 'Failed to save profile');
            }
        } catch (error) {
            Alert.alert('Error', 'Failed to save profile. Please try again.');
            console.error('Save error:', error);
        } finally {
            setSaving(false);
        }
    };

    // Exit animation function
    const exitAnimation = (callback) => {
        setIsExiting(true);
        Animated.parallel([
            Animated.timing(pageOpacity, {
                toValue: 0,
                duration: 600,
                easing: Easing.in(Easing.cubic),
                useNativeDriver: true,
            }),
            Animated.timing(pageTranslateY, {
                toValue: -30,
                duration: 600,
                easing: Easing.in(Easing.cubic),
                useNativeDriver: true,
            })
        ]).start(() => {
            if (callback) callback();
        });
    };

    // Logout function
    const handleLogout = () => {
        startLogout(); // Trigger loading screen
        exitAnimation(() => {
            logout();
            navigateTo('SignIn');
        });
    };

    const ToggleSwitch = ({ isOn, onToggle }) => {
        const thumbPosition = useRef(new Animated.Value(isOn ? 1 : 0)).current;
        const switchBackground = useRef(new Animated.Value(isOn ? 1 : 0)).current;

        useEffect(() => {
            Animated.parallel([
                Animated.timing(thumbPosition, {
                    toValue: isOn ? 1 : 0,
                    duration: 200,
                    easing: Easing.out(Easing.quad),
                    useNativeDriver: false,
                }),
                Animated.timing(switchBackground, {
                    toValue: isOn ? 1 : 0,
                    duration: 200,
                    easing: Easing.out(Easing.quad),
                    useNativeDriver: false,
                }),
            ]).start();
        }, [isOn]);

        const thumbTranslateX = thumbPosition.interpolate({
            inputRange: [0, 1],
            outputRange: [0, 36], // 72px width - 28px thumb width - 8px total margin = 36px movement
        });

        const backgroundColor = switchBackground.interpolate({
            inputRange: [0, 1],
            outputRange: ['rgba(0, 0, 0, 0)', 'rgba(255, 255, 255, 0.20)'],
        });

        return (
            <TouchableOpacity
                style={[styles.toggleSwitch, isOn && styles.toggleSwitchOn]}
                onPress={onToggle}
            >
                <Animated.View style={[styles.toggleSwitchBackground, { backgroundColor }]} />
                <Animated.View
                    style={[
                        styles.toggleThumb,
                        isOn && styles.toggleThumbOn,
                        { transform: [{ translateX: thumbTranslateX }] }
                    ]}
                />
            </TouchableOpacity>
        );
    };

    return (
        <View style={styles.container}>
            <View style={styles.background} />
            <View style={[styles.splashContainer, { 
                paddingTop: Platform.OS === 'web' ? (totalHeaderHeight + responsive(12, 20, 32)) : responsive(12, 20, 16) 
            }]}>
                <NavigationHeader />

                <Animated.View 
                    style={[
                        styles.mainContent, 
                        { 
                            opacity: pageOpacity,
                            transform: [{ translateY: pageTranslateY }]
                        }
                    ]}
                >
                    <BlurView tint="dark" style={styles.mainContentBlur}>
                        <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
                            {/* Tabs */}
                            <View style={{ alignSelf: 'stretch', flexDirection: 'row', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                                <TouchableOpacity onPress={() => setActiveTab('settings')} style={[styles.tabBtn, activeTab === 'settings' && styles.tabBtnActive]}>
                                    <Text style={[styles.tabBtnText, activeTab === 'settings' && styles.tabBtnTextActive]}>Settings</Text>
                                </TouchableOpacity>
                                <TouchableOpacity onPress={() => setActiveTab('betslips')} style={[styles.tabBtn, activeTab === 'betslips' && styles.tabBtnActive]}>
                                    <Text style={[styles.tabBtnText, activeTab === 'betslips' && styles.tabBtnTextActive]}>My BetSlips</Text>
                                </TouchableOpacity>
                                <TouchableOpacity onPress={() => setActiveTab('savedQueries')} style={[styles.tabBtn, activeTab === 'savedQueries' && styles.tabBtnActive]}>
                                    <Text style={[styles.tabBtnText, activeTab === 'savedQueries' && styles.tabBtnTextActive]}>Saved Queries</Text>
                                </TouchableOpacity>
                                <TouchableOpacity onPress={() => setActiveTab('queryHistory')} style={[styles.tabBtn, activeTab === 'queryHistory' && styles.tabBtnActive]}>
                                    <Text style={[styles.tabBtnText, activeTab === 'queryHistory' && styles.tabBtnTextActive]}>Query History</Text>
                                </TouchableOpacity>
                                <TouchableOpacity onPress={() => setActiveTab('watchedFixtures')} style={[styles.tabBtn, activeTab === 'watchedFixtures' && styles.tabBtnActive]}>
                                    <Text style={[styles.tabBtnText, activeTab === 'watchedFixtures' && styles.tabBtnTextActive]}>Watched Fixtures</Text>
                                </TouchableOpacity>
                            </View>

                            {activeTab === 'betslips' ? (
                              <View style={styles.settingsContainer}>
                                <Text style={styles.settingsTitle}>My BetSlips</Text>
                                {betSlipsLoading ? (
                                  <Text style={{ color: 'rgba(255,255,255,0.7)' }}>Loading…</Text>
                                ) : betSlips.length ? (
                                  <View style={{ alignSelf: 'stretch', gap: 12 }}>
                                    {betSlips.map((b) => (
                                      <View key={b.id} style={{ padding: 16, borderWidth: 1, borderColor: 'rgba(162, 89, 255, 0.2)', borderRadius: 12, backgroundColor: 'rgba(0,0,0,0.3)', marginBottom: 12 }}>
                                        {/* Header with timestamp and query */}
                                        <View style={{ marginBottom: 12, paddingBottom: 8, borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.1)' }}>
                                          <Text style={{ color: '#EBDEFF', fontWeight: '700', fontSize: 15, marginBottom: 4 }}>
                                            {new Date(b?.createdAt?.toDate ? b.createdAt.toDate() : Date.now()).toLocaleString()}
                                          </Text>
                                          {b.query ? (
                                            <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>Query: {b.query}</Text>
                                          ) : null}
                                        </View>
                                        
                                        {/* Legs */}
                                        {Array.isArray(b.legs) && b.legs.length ? b.legs.map((l, idx) => (
                                          <View key={`${b.id}-${idx}`} style={{ 
                                            marginBottom: idx < b.legs.length - 1 ? 12 : 0,
                                            padding: 12,
                                            backgroundColor: 'rgba(255,255,255,0.04)',
                                            borderRadius: 8,
                                            borderLeftWidth: 3,
                                            borderLeftColor: l.is_live ? '#4ade80' : '#A259FF'
                                          }}>
                                            {/* Game info (if available) */}
                                            {(l.home_team || l.away_team) ? (
                                              <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8, gap: 8 }}>
                                                {l.away_logo ? (
                                                  <Image source={{ uri: l.away_logo }} style={{ width: 20, height: 20 }} resizeMode="contain" />
                                                ) : null}
                                                <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: 14, fontWeight: '600' }}>
                                                  {l.away_team || 'Away'} @ {l.home_team || 'Home'}
                                                </Text>
                                                {l.home_logo ? (
                                                  <Image source={{ uri: l.home_logo }} style={{ width: 20, height: 20 }} resizeMode="contain" />
                                                ) : null}
                                                {l.is_live ? (
                                                  <View style={{ backgroundColor: '#4ade80', paddingHorizontal: 6, paddingVertical: 2, borderRadius: 4, marginLeft: 'auto' }}>
                                                    <Text style={{ color: '#000', fontSize: 10, fontWeight: '700' }}>LIVE</Text>
                                                  </View>
                                                ) : null}
                                              </View>
                                            ) : null}
                                            
                                            {/* Market and selection */}
                                            <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 6 }}>
                                              {l.market ? (
                                                <View style={{ backgroundColor: 'rgba(162, 89, 255, 0.15)', paddingHorizontal: 8, paddingVertical: 4, borderRadius: 4 }}>
                                                  <Text style={{ color: '#EBDEFF', fontSize: 12, fontWeight: '600' }}>{l.market}</Text>
                                                </View>
                                              ) : null}
                                              <Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: 14 }}>
                                                {l.selection || ''}
                                              </Text>
                                            </View>
                                            
                                            {/* Price and sportsbook */}
                                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                              {(l.price !== undefined && l.price !== null) ? (
                                                <Text style={{ color: l.price > 0 ? '#4ade80' : 'rgba(255,255,255,0.7)', fontSize: 14, fontWeight: '700' }}>
                                                  {l.price > 0 ? '+' : ''}{l.price}
                                                </Text>
                                              ) : null}
                                              
                                              {l.sportsbook ? (
                                                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                                                  {l.sportsbook_logo ? (
                                                    <Image source={{ uri: l.sportsbook_logo }} style={{ width: 16, height: 16 }} resizeMode="contain" />
                                                  ) : null}
                                                  <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>{l.sportsbook}</Text>
                                                </View>
                                              ) : null}
                                              
                                              {l.deep_link ? (
                                                <TouchableOpacity 
                                                  onPress={() => { try { if (Platform.OS === 'web') window.open(l.deep_link, '_blank'); } catch {} }}
                                                  style={{ marginLeft: 'auto', backgroundColor: 'rgba(162, 89, 255, 0.2)', paddingHorizontal: 10, paddingVertical: 5, borderRadius: 6 }}
                                                >
                                                  <Text style={{ color: '#EBDEFF', fontSize: 12, fontWeight: '600' }}>View on Sportsbook</Text>
                                                </TouchableOpacity>
                                              ) : null}
                                            </View>
                                          </View>
                                        )) : (
                                          <Text style={{ color: 'rgba(255,255,255,0.7)' }}>No legs saved</Text>
                                        )}
                                      </View>
                                    ))}
                                  </View>
                                ) : (
                                  <Text style={{ color: 'rgba(255,255,255,0.7)' }}>No saved betslips yet.</Text>
                                )}
                              </View>
                            ) : activeTab === 'savedQueries' ? (
                              <View style={styles.settingsContainer}>
                                <Text style={styles.settingsTitle}>Saved Queries</Text>
                                {savedQueriesLoading ? (
                                  <Text style={{ color: 'rgba(255,255,255,0.7)' }}>Loading…</Text>
                                ) : savedQueries.length ? (
                                  <View style={{ alignSelf: 'stretch', gap: 12 }}>
                                    {savedQueries.map((sq) => (
                                      <View key={sq.id} style={{ 
                                        padding: 16, 
                                        borderWidth: 1, 
                                        borderColor: 'rgba(139, 92, 246, 0.3)', 
                                        borderRadius: 12, 
                                        backgroundColor: 'rgba(139, 92, 246, 0.08)' 
                                      }}>
                                        {/* Header */}
                                        <View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
                                          <View style={{ flex: 1 }}>
                                            <Text style={{ color: '#EBDEFF', fontWeight: '700', fontSize: 16, marginBottom: 4 }}>
                                              {sq.query}
                                            </Text>
                                            {sq.teamName && (
                                              <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, marginTop: 4 }}>
                                                {sq.teamName} · {sq.fixtureCount} game{sq.fixtureCount !== 1 ? 's' : ''}
                                              </Text>
                                            )}
                                            <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, marginTop: 6 }}>
                                              Saved {sq.createdAt?.toDate ? new Date(sq.createdAt.toDate()).toLocaleDateString() : 'recently'}
                                            </Text>
                                          </View>
                                          
                                          {/* Delete button */}
                                          <TouchableOpacity
                                            onPress={async () => {
                                              try {
                                                await deleteDoc(doc(db, 'saved_queries', sq.id));
                                                setSavedQueries(prev => prev.filter(q => q.id !== sq.id));
                                              } catch (error) {
                                                console.error('Error deleting query:', error);
                                                Alert.alert('Error', 'Failed to delete query');
                                              }
                                            }}
                                            style={{
                                              padding: 8,
                                              borderRadius: 8,
                                              backgroundColor: 'rgba(255,255,255,0.08)',
                                            }}
                                          >
                                            <Text style={{ fontSize: 16, color: 'rgba(255,255,255,0.6)' }}>✕</Text>
                                          </TouchableOpacity>
                                        </View>
                                        
                                        {/* Action buttons */}
                                        <View style={{ flexDirection: 'row', gap: 8 }}>
                                          <TouchableOpacity
                                            onPress={() => navigateTo('SgpFlow', { payload: { query: sq.query } })}
                                            style={{
                                              flex: 1,
                                              paddingVertical: 10,
                                              paddingHorizontal: 16,
                                              borderRadius: 8,
                                              backgroundColor: 'rgba(139, 92, 246, 0.8)',
                                              alignItems: 'center',
                                            }}
                                          >
                                            <Text style={{ color: '#fff', fontSize: 14, fontWeight: '600' }}>Search Again</Text>
                                          </TouchableOpacity>
                                          
                                          {sq.notifyWhenAvailable && (
                                            <View style={{
                                              paddingVertical: 10,
                                              paddingHorizontal: 12,
                                              borderRadius: 8,
                                              backgroundColor: 'rgba(74, 222, 128, 0.15)',
                                              borderWidth: 1,
                                              borderColor: 'rgba(74, 222, 128, 0.3)',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                            }}>
                                              <Text style={{ color: '#4ade80', fontSize: 12, fontWeight: '600' }}>🔔 Watching</Text>
                                            </View>
                                          )}
                                        </View>
                                      </View>
                                    ))}
                                  </View>
                                ) : (
                                  <Text style={{ color: 'rgba(255,255,255,0.7)' }}>No saved queries yet. When you save a query, it will appear here.</Text>
                                )}
                              </View>
                            ) : activeTab === 'queryHistory' ? (
                              <View style={styles.settingsContainer}>
                                <Text style={styles.settingsTitle}>Query History</Text>
                                <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 14, marginBottom: 16, textAlign: 'center' }}>
                                  Your past queries with results and metrics. Re-run or copy queries from here.
                                </Text>
                                
                                {historyLoading ? (
                                  <Text style={{ color: 'rgba(255,255,255,0.7)' }}>Loading query history…</Text>
                                ) : queryHistory.length ? (
                                  <View style={{ alignSelf: 'stretch', gap: 12 }}>
                                    {/* Clear All Button */}
                                    <TouchableOpacity
                                      onPress={() => {
                                        Alert.alert(
                                          'Clear History',
                                          'Are you sure you want to clear all query history?',
                                          [
                                            { text: 'Cancel', style: 'cancel' },
                                            { text: 'Clear All', style: 'destructive', onPress: clearHistory }
                                          ]
                                        );
                                      }}
                                      style={{
                                        alignSelf: 'flex-end',
                                        paddingVertical: 8,
                                        paddingHorizontal: 12,
                                        borderRadius: 8,
                                        backgroundColor: 'rgba(248, 113, 113, 0.15)',
                                        borderWidth: 1,
                                        borderColor: 'rgba(248, 113, 113, 0.3)',
                                      }}
                                    >
                                      <Text style={{ color: '#f87171', fontSize: 12, fontWeight: '600' }}>Clear All History</Text>
                                    </TouchableOpacity>
                                    
                                    {queryHistory.map((entry) => (
                                      <View key={entry.id} style={{ 
                                        padding: 16, 
                                        borderWidth: 1, 
                                        borderColor: entry.status === 'completed' ? 'rgba(74, 222, 128, 0.3)' : 'rgba(248, 113, 113, 0.3)', 
                                        borderRadius: 12, 
                                        backgroundColor: entry.status === 'completed' ? 'rgba(74, 222, 128, 0.08)' : 'rgba(248, 113, 113, 0.08)' 
                                      }}>
                                        {/* Header */}
                                        <View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
                                          <View style={{ flex: 1 }}>
                                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                              <Ionicons 
                                                name={entry.status === 'completed' ? 'checkmark-circle' : 'alert-circle'} 
                                                size={16} 
                                                color={entry.status === 'completed' ? '#4ade80' : '#f87171'} 
                                              />
                                              <Text style={{ color: '#EBDEFF', fontWeight: '700', fontSize: 16, flex: 1 }} numberOfLines={2}>
                                                {entry.prompt || 'Query'}
                                              </Text>
                                            </View>
                                            
                                            {/* Metrics */}
                                            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                                              <View style={styles.metricChip}>
                                                <Ionicons name="layers" size={12} color="#c4b5fd" />
                                                <Text style={styles.metricChipText}>{entry.resultsCount || 0} results</Text>
                                              </View>
                                              {entry.sgpCount > 0 && (
                                                <View style={styles.metricChip}>
                                                  <Ionicons name="git-merge" size={12} color="#c4b5fd" />
                                                  <Text style={styles.metricChipText}>{entry.sgpCount} SGPs</Text>
                                                </View>
                                              )}
                                              {entry.cached && (
                                                <View style={[styles.metricChip, { backgroundColor: 'rgba(251, 191, 36, 0.15)', borderColor: 'rgba(251, 191, 36, 0.3)' }]}>
                                                  <Ionicons name="flash" size={12} color="#fbbf24" />
                                                  <Text style={[styles.metricChipText, { color: '#fbbf24' }]}>Cached</Text>
                                                </View>
                                              )}
                                              {entry.durationMs && (
                                                <View style={styles.metricChip}>
                                                  <Ionicons name="time" size={12} color="#c4b5fd" />
                                                  <Text style={styles.metricChipText}>{(entry.durationMs / 1000).toFixed(1)}s</Text>
                                                </View>
                                              )}
                                            </View>
                                            
                                            <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, marginTop: 8 }}>
                                              {entry.createdAt ? new Date(entry.createdAt).toLocaleString() : 'Recently'}
                                            </Text>
                                          </View>
                                          
                                          {/* Delete button */}
                                          <TouchableOpacity
                                            onPress={() => deleteFromHistory(entry.id)}
                                            style={{
                                              padding: 8,
                                              borderRadius: 8,
                                              backgroundColor: 'rgba(255,255,255,0.08)',
                                            }}
                                          >
                                            <Text style={{ fontSize: 16, color: 'rgba(255,255,255,0.6)' }}>✕</Text>
                                          </TouchableOpacity>
                                        </View>
                                        
                                        {/* Action buttons */}
                                        <View style={{ flexDirection: 'row', gap: 8 }}>
                                          <TouchableOpacity
                                            onPress={() => {
                                              // Store in session and navigate
                                              if (Platform.OS === 'web') {
                                                try {
                                                  window.sessionStorage.setItem('sgp:lastQuery', entry.prompt);
                                                } catch (e) {}
                                              }
                                              navigateTo('SgpFlow', { payload: { query: entry.prompt } });
                                            }}
                                            style={{
                                              flex: 1,
                                              paddingVertical: 10,
                                              paddingHorizontal: 16,
                                              borderRadius: 8,
                                              backgroundColor: 'rgba(139, 92, 246, 0.8)',
                                              alignItems: 'center',
                                              flexDirection: 'row',
                                              justifyContent: 'center',
                                              gap: 6,
                                            }}
                                          >
                                            <Ionicons name="refresh" size={14} color="#fff" />
                                            <Text style={{ color: '#fff', fontSize: 14, fontWeight: '600' }}>Run Again</Text>
                                          </TouchableOpacity>
                                          
                                          <TouchableOpacity
                                            onPress={() => {
                                              // Copy to clipboard
                                              if (Platform.OS === 'web') {
                                                try {
                                                  navigator.clipboard.writeText(entry.prompt);
                                                  window.dispatchEvent(new CustomEvent('toast', { detail: { type: 'success', text: 'Query copied to clipboard!' } }));
                                                } catch (e) {
                                                  console.error('Failed to copy:', e);
                                                }
                                              } else {
                                                Clipboard.setString(entry.prompt);
                                                Alert.alert('Copied', 'Query copied to clipboard');
                                              }
                                            }}
                                            style={{
                                              paddingVertical: 10,
                                              paddingHorizontal: 16,
                                              borderRadius: 8,
                                              backgroundColor: 'rgba(255,255,255,0.1)',
                                              borderWidth: 1,
                                              borderColor: 'rgba(255,255,255,0.2)',
                                              alignItems: 'center',
                                              flexDirection: 'row',
                                              justifyContent: 'center',
                                              gap: 6,
                                            }}
                                          >
                                            <Ionicons name="copy" size={14} color="rgba(255,255,255,0.7)" />
                                            <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 14, fontWeight: '600' }}>Copy</Text>
                                          </TouchableOpacity>
                                        </View>
                                        
                                        {/* Error message if failed */}
                                        {entry.status === 'error' && entry.error && (
                                          <View style={{ marginTop: 12, padding: 10, backgroundColor: 'rgba(248, 113, 113, 0.1)', borderRadius: 8 }}>
                                            <Text style={{ color: '#f87171', fontSize: 12 }}>Error: {entry.error}</Text>
                                          </View>
                                        )}
                                      </View>
                                    ))}
                                  </View>
                                ) : (
                                  <View style={{ alignItems: 'center', padding: 32 }}>
                                    <Ionicons name="time-outline" size={48} color="rgba(255,255,255,0.3)" />
                                    <Text style={{ color: 'rgba(255,255,255,0.7)', marginTop: 16, textAlign: 'center' }}>
                                      No query history yet. Run some queries and they'll appear here!
                                    </Text>
                                  </View>
                                )}
                              </View>
                            ) : activeTab === 'watchedFixtures' ? (
                              <View style={styles.settingsContainer}>
                                <Text style={styles.settingsTitle}>Watched Fixtures</Text>
                                <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 14, marginBottom: 16 }}>
                                  You'll be notified when odds become available for these fixtures.
                                </Text>
                                {watchedFixturesLoading ? (
                                  <Text style={{ color: 'rgba(255,255,255,0.7)' }}>Loading…</Text>
                                ) : watchedFixtures.length ? (
                                  <View style={{ alignSelf: 'stretch', gap: 12 }}>
                                    {watchedFixtures.map((fixture) => (
                                      <View key={fixture.id} style={{ 
                                        padding: 16, 
                                        borderWidth: 1, 
                                        borderColor: 'rgba(255, 193, 7, 0.3)', 
                                        borderRadius: 12, 
                                        backgroundColor: 'rgba(255, 193, 7, 0.08)' 
                                      }}>
                                        {/* Header */}
                                        <View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
                                          <View style={{ flex: 1 }}>
                                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                              <View style={{
                                                backgroundColor: 'rgba(76, 175, 80, 0.2)',
                                                paddingHorizontal: 10,
                                                paddingVertical: 4,
                                                borderRadius: 12,
                                                borderWidth: 1,
                                                borderColor: 'rgba(76, 175, 80, 0.5)',
                                              }}>
                                                <Text style={{ color: '#4CAF50', fontSize: 11, fontWeight: '700' }}>👁️ WATCHING</Text>
                                              </View>
                                              <Text style={{ color: '#FFC107', fontSize: 11, fontWeight: '600' }}>
                                                {fixture.sport?.toUpperCase() || 'BASKETBALL'} · {fixture.league?.toUpperCase() || 'NBA'}
                                              </Text>
                                            </View>
                                            <Text style={{ color: '#EBDEFF', fontWeight: '700', fontSize: 16, marginBottom: 4 }}>
                                              {fixture.fixtureTitle}
                                            </Text>
                                            {fixture.startDate && (
                                              <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, marginTop: 4 }}>
                                                {new Date(fixture.startDate).toLocaleString()}
                                              </Text>
                                            )}
                                            <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, marginTop: 6 }}>
                                              Watching since {fixture.createdAt?.toDate ? new Date(fixture.createdAt.toDate()).toLocaleDateString() : 'recently'}
                                            </Text>
                                          </View>
                                          
                                          {/* Delete button */}
                                          <TouchableOpacity
                                            onPress={async () => {
                                              Alert.alert(
                                                'Stop Watching',
                                                'Are you sure you want to stop watching this fixture? You won\'t be notified when odds are posted.',
                                                [
                                                  { text: 'Cancel', style: 'cancel' },
                                                  {
                                                    text: 'Stop Watching',
                                                    style: 'destructive',
                                                    onPress: async () => {
                                                      try {
                                                        await deleteDoc(doc(db, 'query_watchers', fixture.id));
                                                        setWatchedFixtures(prev => prev.filter(f => f.id !== fixture.id));
                                                      } catch (error) {
                                                        console.error('Error deleting watcher:', error);
                                                        Alert.alert('Error', 'Failed to stop watching fixture');
                                                      }
                                                    }
                                                  }
                                                ]
                                              );
                                            }}
                                            style={{
                                              padding: 8,
                                              borderRadius: 8,
                                              backgroundColor: 'rgba(244, 67, 54, 0.15)',
                                              borderWidth: 1,
                                              borderColor: 'rgba(244, 67, 54, 0.3)',
                                            }}
                                          >
                                            <Text style={{ fontSize: 14, color: '#FF5252', fontWeight: '600' }}>✕</Text>
                                          </TouchableOpacity>
                                        </View>
                                        
                                        {/* Info */}
                                        <View style={{ 
                                          backgroundColor: 'rgba(0,0,0,0.2)', 
                                          padding: 12, 
                                          borderRadius: 8,
                                          borderWidth: 1,
                                          borderColor: 'rgba(255,255,255,0.05)'
                                        }}>
                                          <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>
                                            💡 We check for odds every 30 minutes. You'll get a notification as soon as they're available.
                                          </Text>
                                          {fixture.lastChecked && (
                                            <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, marginTop: 6 }}>
                                              Last checked: {new Date(fixture.lastChecked.toDate ? fixture.lastChecked.toDate() : Date.now()).toLocaleTimeString()}
                                            </Text>
                                          )}
                                        </View>
                                      </View>
                                    ))}
                                  </View>
                                ) : (
                                  <Text style={{ color: 'rgba(255,255,255,0.7)' }}>No watched fixtures yet. When you watch a fixture, it will appear here.</Text>
                                )}
                              </View>
                            ) : (
                            <View style={styles.settingsContainer}>
                                <Text style={styles.settingsTitle}>Settings</Text>

                                {/* Monthly Query Usage Card */}
                                <View style={styles.usageCard}>
                                    <View style={styles.usageHeader}>
                                        <View style={styles.usageHeaderLeft}>
                                            <Ionicons name="analytics-outline" size={20} color="#a78bfa" />
                                            <Text style={styles.usageTitle}>Monthly Query Usage</Text>
                                        </View>
                                        <View style={[styles.usagePlanBadge, 
                                            isAdmin && styles.usagePlanBadgeAdmin,
                                            queryUsage.planType === 'PRO' && styles.usagePlanBadgePro,
                                            queryUsage.planType === 'ULTIMATE' && styles.usagePlanBadgeUltimate
                                        ]}>
                                            <Text style={styles.usagePlanText}>{isAdmin ? 'ADMIN' : queryUsage.planType}</Text>
                                        </View>
                                    </View>
                                    
                                    {queryUsageLoading ? (
                                        <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>Loading usage...</Text>
                                    ) : isAdmin ? (
                                        // Admin users get unlimited queries
                                        <View style={styles.usageStats}>
                                            <Text style={styles.usageCount}>
                                                <Text style={styles.usageCountHighlight}>{queryUsage.queriesUsed}</Text>
                                                <Text style={styles.usageCountDivider}> / </Text>
                                                <Text style={{ color: '#fbbf24' }}>∞</Text>
                                            </Text>
                                            <Text style={styles.usageLabel}>queries used this month</Text>
                                            <Text style={{ color: '#4ade80', fontSize: 12, marginTop: 8 }}>
                                                ✓ Unlimited queries (Admin)
                                            </Text>
                                        </View>
                                    ) : (
                                        <>
                                            <View style={styles.usageStats}>
                                                <Text style={styles.usageCount}>
                                                    <Text style={styles.usageCountHighlight}>{queryUsage.queriesUsed}</Text>
                                                    <Text style={styles.usageCountDivider}> / </Text>
                                                    <Text>{queryUsage.queryLimit}</Text>
                                                </Text>
                                                <Text style={styles.usageLabel}>queries used this month</Text>
                                            </View>
                                            
                                            {/* Progress bar */}
                                            <View style={styles.usageProgressContainer}>
                                                <View style={styles.usageProgressBg}>
                                                    <LinearGradient
                                                        colors={
                                                            queryUsage.queriesUsed >= queryUsage.queryLimit 
                                                                ? ['#f87171', '#ef4444']
                                                                : queryUsage.queriesUsed >= queryUsage.queryLimit * 0.8
                                                                    ? ['#fbbf24', '#f59e0b']
                                                                    : ['#a78bfa', '#8b5cf6']
                                                        }
                                                        start={{ x: 0, y: 0 }}
                                                        end={{ x: 1, y: 0 }}
                                                        style={[
                                                            styles.usageProgressFill,
                                                            { width: `${Math.min((queryUsage.queriesUsed / queryUsage.queryLimit) * 100, 100)}%` }
                                                        ]}
                                                    />
                                                </View>
                                                <Text style={styles.usagePercentage}>
                                                    {Math.round((queryUsage.queriesUsed / queryUsage.queryLimit) * 100)}%
                                                </Text>
                                            </View>
                                            
                                            {/* Remaining / upgrade prompt */}
                                            <View style={styles.usageFooter}>
                                                {queryUsage.queriesUsed >= queryUsage.queryLimit ? (
                                                    <Text style={styles.usageLimitReached}>
                                                        ⚠️ Limit reached - Upgrade for more queries
                                                    </Text>
                                                ) : (
                                                    <Text style={styles.usageRemaining}>
                                                        {queryUsage.queryLimit - queryUsage.queriesUsed} queries remaining
                                                    </Text>
                                                )}
                                                {queryUsage.planType === 'FREE' && (
                                                    <TouchableOpacity 
                                                        onPress={() => navigateTo('Plans')}
                                                        style={styles.usageUpgradeBtn}
                                                    >
                                                        <Text style={styles.usageUpgradeBtnText}>Upgrade</Text>
                                                        <Ionicons name="arrow-forward" size={14} color="#a78bfa" />
                                                    </TouchableOpacity>
                                                )}
                                            </View>
                                        </>
                                    )}
                                </View>

                                {/* Profile Section */}
                                <Animated.View style={[styles.profileSection, { opacity: profileSectionOpacity }]}>
                                    <TouchableOpacity style={styles.profileImageContainer} onPress={Platform.OS === 'web' ? handleProfilePhotoUpload : pickImage}>
                                        <View style={styles.profileImageBackground}>
                                            <LinearGradient
                                                colors={['rgba(255, 255, 255, 0.15)', 'rgba(255, 255, 255, 0.3)']}
                                                start={{ x: 0, y: 0 }}
                                                end={{ x: 1, y: 1 }}
                                                style={styles.profileImageGradient}
                                            />
                                            {profilePhoto ? (
                                                <Image
                                                    source={{ uri: profilePhoto }}
                                                    style={styles.profileImage}
                                                />
                                            ) : (
                                                <View style={styles.profileImagePlaceholder}>
                                                    <Ionicons name="camera-outline" size={20} color="rgba(255, 255, 255, 0.60)" />
                                                    <Text style={styles.uploadText}>Upload Photo</Text>
                                                </View>
                                            )}
                                        </View>
                                    </TouchableOpacity>
                                    {profilePhoto ? (
                                        <TouchableOpacity onPress={handleProfilePhotoRemove} style={{ marginTop: 8 }}>
                                            <Text style={{ color: 'rgba(255,100,100,0.9)', fontSize: 13, textDecorationLine: 'underline' }}>Remove Photo</Text>
                                        </TouchableOpacity>
                                    ) : null}
                                    <View style={styles.profileInfo}>
                                        {/* Removed username field */}
                                    </View>
                                </Animated.View>
                                {subscriptionPlan && (
                                    <Animated.View style={[styles.section, { opacity: profileSectionOpacity }]}>
                                        <View style={[styles.inputRow, { alignItems: 'center' }]}>
                                            <Text style={styles.planText}>
                                                {subscriptionPlan === 'BASIC' ? 'Basic Plan' :
                                                    subscriptionPlan === 'PRO' ? 'Pro Plan' :
                                                        subscriptionPlan === 'ULTIMATE' ? 'Ultimate Plan' :
                                                            subscriptionPlan === 'ACTIVE' ? 'Active Plan' : 'Free Plan'}
                                            </Text>
                                            {subscriptionPlan && subscriptionPlan !== 'FREE' && (
                                                <TouchableOpacity
                                                    style={styles.manageSubscriptionButton}
                                                    onPress={() => window.open('https://billing.stripe.com/p/login/test_fZu00c3YtbXq5RA6G53Nm00', '_blank')}
                                                >
                                                    <Text style={styles.manageSubscriptionButtonText}>Manage Subscription</Text>
                                                </TouchableOpacity>
                                            )}
                                        </View>
                                    </Animated.View>
                                )}
                                

                                {/* Account Details Section */}
                                <Animated.View style={[styles.section, { opacity: accountSectionOpacity }]}>
                                    <Text style={styles.sectionTitle}>Account Details</Text>
                                    <View style={styles.inputRow}>
                                        <View style={styles.inputGroup}>
                                            <Text style={styles.inputLabel}>Email</Text>
                                            <AnimatedInput
                                                value={email}
                                                onChangeText={setEmail}
                                                placeholder="Enter your email"
                                                style={styles.inputContainer}
                                            />
                                        </View>
                                    </View>
                                    
                                    {/* Reset Password Section */}
                                    <ResetPasswordSection email={email} />
                                </Animated.View>

                                {/* Preferences Section */}
                                <Animated.View style={[styles.section, { opacity: preferencesSectionOpacity }]}>
                                    <Text style={styles.sectionTitle}>Preferences</Text>

                                    {/* Unified Sportsbook Filters & Limits */}
                                    <View style={styles.preferenceItem}>
                                        <View style={styles.sportsbookFiltersContainer}>
                                            <Text style={styles.preferenceTitle}>Sportsbook Filters & Betting Limits</Text>
                                            <Text style={styles.preferenceDescription}>
                                                Select sportsbooks to include in searches (checked = active filter). 
                                                Set optional betting limits for each book. These filters apply to all searches but limits are for your reference only.
                                            </Text>
                                            <View style={{ flexDirection: 'row', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                                                <TouchableOpacity onPress={() => setShowAllBooks(false)} style={[styles.tag, !showAllBooks ? styles.tagActive : null]}>
                                                    <Text style={[styles.tagText, !showAllBooks ? styles.tagTextActive : null]}>Top 20</Text>
                                                </TouchableOpacity>
                                                <TouchableOpacity onPress={() => setShowAllBooks(true)} style={[styles.tag, showAllBooks ? styles.tagActive : null]}>
                                                    <Text style={[styles.tagText, showAllBooks ? styles.tagTextActive : null]}>Show All ({allSportsbooks.length})</Text>
                                                </TouchableOpacity>
                                                <TouchableOpacity 
                                                    onPress={clearAllSportsbookFilters} 
                                                    style={[styles.tag, { backgroundColor: 'rgba(248, 113, 113, 0.2)', borderColor: '#f87171' }]}
                                                >
                                                    <Text style={[styles.tagText, { color: '#f87171' }]}>Clear All Filters</Text>
                                                </TouchableOpacity>
                                                {selectedSportsbooks.length > 0 && (
                                                    <Text style={{ color: '#4ade80', fontSize: 13, alignSelf: 'center', fontWeight: '600' }}>
                                                        {selectedSportsbooks.length} sportsbook{selectedSportsbooks.length !== 1 ? 's' : ''} selected
                                                    </Text>
                                                )}
                                            </View>
                                            <View style={styles.sportsbookFiltersGrid}>
                                                {(() => {
                                                    // Define the 35 active sportsbooks from cache
                                                    const activeSportsbooks = [
                                                        'DraftKings', 'FanDuel', 'BetMGM', 'Caesars', 'ESPNBet',
                                                        'BetRivers', 'Fanatics', 'BetOnline', 'Bovada', 'HardRock',
                                                        'Superbook', 'MyBookie', 'GoldenNugget', 'WilliamHill', 'BallyBet',
                                                        'Pinnacle', 'Betfred', 'BetUS', 'Borgata', 'BetPARX',
                                                        'Circa Vegas', 'Novig', 'Underdog Sportsbook', 'Sleeper',
                                                        'Prophet X', 'Kalshi', 'BookMaker', 'Resorts World Bet', 'Tipico',
                                                        'Rebet', 'Fliff', 'Betr', 'WynnBET', 'STN Sports'
                                                    ];
                                                    
                                                    const booksToDisplay = showAllBooks ? allSportsbooks : allSportsbooks.slice(0, 20);
                                                    const limitedBooks = showAllFilters ? booksToDisplay : booksToDisplay.slice(0, 8);
                                                    
                                                    // Separate active from coming soon
                                                    const activeBooks = limitedBooks.filter(book => activeSportsbooks.includes(book));
                                                    const comingSoonBooks = limitedBooks.filter(book => !activeSportsbooks.includes(book));
                                                    
                                                    return (
                                                        <>
                                                            {/* Active Sportsbooks */}
                                                            {activeBooks.map((sportsbook, index) => (
                                                                <View key={`active-${index}`} style={styles.sportsbookFilterCard}>
                                                        <TouchableOpacity
                                                            style={styles.sportsbookFilterCheckbox}
                                                            onPress={() => toggleSportsbook(sportsbook)}
                                                        >
                                                            <View style={[
                                                                styles.customCheckbox,
                                                                selectedSportsbooks.includes(sportsbook) && styles.customCheckboxSelected
                                                            ]} />
                                                        </TouchableOpacity>
                                                        <Text style={[
                                                            styles.sportsbookFilterName,
                                                            selectedSportsbooks.includes(sportsbook) && { fontWeight: '700', color: '#fff' }
                                                        ]}>
                                                            {sportsbook}
                                                        </Text>
                                                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, marginTop: 4 }}>
                                                            <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>$</Text>
                                                            <SimpleInput
                                                                value={sportsbookLimits[sportsbook] || ''}
                                                                onChangeText={(text) => updateSportsbookLimit(sportsbook, text)}
                                                                style={styles.sportsbookFilterLimitInput}
                                                                placeholder="Limit"
                                                            />
                                                        </View>
                                                    </View>
                                                ))}
                                                            
                                                            {/* Coming Soon Sportsbooks (greyed out) */}
                                                            {comingSoonBooks.length > 0 && (
                                                                <>
                                                                    {activeBooks.length > 0 && (
                                                                        <View style={{ gridColumn: '1 / -1', marginTop: 12, marginBottom: 4 }}>
                                                                            <Text style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, fontWeight: '600', textTransform: 'uppercase', letterSpacing: 1 }}>
                                                                                Coming Soon
                                                                            </Text>
                                                                        </View>
                                                                    )}
                                                                    {comingSoonBooks.map((sportsbook, index) => (
                                                                        <View key={`coming-soon-${index}`} style={[styles.sportsbookFilterCard, { opacity: 0.4 }]}>
                                                                            <View style={styles.sportsbookFilterCheckbox}>
                                                                                <View style={[
                                                                                    styles.customCheckbox,
                                                                                    { backgroundColor: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.1)' }
                                                                                ]} />
                                                                            </View>
                                                                            <Text style={[
                                                                                styles.sportsbookFilterName,
                                                                                { color: 'rgba(255,255,255,0.5)' }
                                                                            ]}>
                                                                                {sportsbook}
                                                                            </Text>
                                                                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, marginTop: 4 }}>
                                                                                <Text style={{ color: 'rgba(255,255,255,0.3)', fontSize: 10 }}>Soon</Text>
                                                                            </View>
                                                                        </View>
                                                                    ))}
                                                                </>
                                                            )}
                                                        </>
                                                    );
                                                })()}
                                            </View>
                                            {!showAllFilters && (showAllBooks ? allSportsbooks : allSportsbooks.slice(0, 20)).length > 8 && (
                                                <TouchableOpacity 
                                                    onPress={() => setShowAllFilters(true)}
                                                    style={styles.showMoreButton}
                                                >
                                                    <Text style={styles.showMoreButtonText}>
                                                        Show More ({(showAllBooks ? allSportsbooks : allSportsbooks.slice(0, 20)).length - 8} remaining)
                                                    </Text>
                                                </TouchableOpacity>
                                            )}
                                            {showAllFilters && (showAllBooks ? allSportsbooks : allSportsbooks.slice(0, 20)).length > 8 && (
                                                <TouchableOpacity 
                                                    onPress={() => setShowAllFilters(false)}
                                                    style={styles.showLessButton}
                                                >
                                                    <Text style={styles.showLessButtonText}>Show Less</Text>
                                                </TouchableOpacity>
                                            )}
                                        </View>
                                    </View>

                                    {/* Display Settings */}
                                    <Text style={styles.preferenceTitle}>Display Settings</Text>

                                    {/* Game Ticker Toggle */}
                                    <View style={styles.settingRow}>
                                        <View style={styles.settingInfo}>
                                            <Text style={styles.settingTitle}>Game Ticker</Text>
                                            <Text style={styles.settingDescription}>Show a scrolling ticker at the top of the page displaying live, upcoming, and recently completed games.</Text>
                                        </View>
                                        <View style={styles.settingValue}>
                                            <LinearGradient
                                                colors={['rgba(255, 255, 255, 0.05)', 'rgba(255, 255, 255, 0.10)']}
                                                start={{ x: 0, y: 0 }}
                                                end={{ x: 0, y: 1 }}
                                                style={styles.settingValueGradient}
                                            />
                                            <ToggleSwitch isOn={isTickerEnabled} onToggle={toggleTicker} />
                                        </View>
                                    </View>
                                    
                                    {/* ⭐ SGP Cache Indicator on Ticker - positioned right after ticker toggle */}
                                    {isTickerEnabled && (
                                        <View style={styles.settingRow}>
                                            <View style={styles.settingInfo}>
                                                <Text style={styles.settingTitle}>Show SGP Indicators</Text>
                                                <Text style={styles.settingDescription}>Display a subtle green "SGP" badge on ticker games that have pre-cached Same Game Parlay results available. Click the badge to view cached SGPs instantly.</Text>
                                            </View>
                                            <View style={styles.settingValue}>
                                                <LinearGradient
                                                    colors={['rgba(255, 255, 255, 0.05)', 'rgba(255, 255, 255, 0.10)']}
                                                    start={{ x: 0, y: 0 }}
                                                    end={{ x: 0, y: 1 }}
                                                    style={styles.settingValueGradient}
                                                />
                                                <ToggleSwitch 
                                                    isOn={showTickerSGPIndicator === true} 
                                                    onToggle={() => {
                                                        if (updateShowTickerSGPIndicator) {
                                                            updateShowTickerSGPIndicator(!showTickerSGPIndicator);
                                                        }
                                                    }} 
                                                />
                                            </View>
                                        </View>
                                    )}
                                    
                                    {/* ⭐ SGP-Only Mode - filter ticker to only show cached SGP games */}
                                    {isTickerEnabled && showTickerSGPIndicator && (
                                        <View style={styles.settingRow}>
                                            <View style={styles.settingInfo}>
                                                <Text style={styles.settingTitle}>SGP-Only Mode</Text>
                                                <Text style={styles.settingDescription}>Only show games with cached SGP results in the ticker. Great for seeing cache coverage at a glance.</Text>
                                            </View>
                                            <View style={styles.settingValue}>
                                                <LinearGradient
                                                    colors={['rgba(255, 255, 255, 0.05)', 'rgba(255, 255, 255, 0.10)']}
                                                    start={{ x: 0, y: 0 }}
                                                    end={{ x: 0, y: 1 }}
                                                    style={styles.settingValueGradient}
                                                />
                                                <ToggleSwitch 
                                                    isOn={tickerSGPOnly === true} 
                                                    onToggle={() => {
                                                        if (updateTickerSGPOnly) {
                                                            updateTickerSGPOnly(!tickerSGPOnly);
                                                        }
                                                    }} 
                                                />
                                            </View>
                                        </View>
                                    )}

                                    {/* Ticker Leagues/Sports Preferences */}
                                    {isTickerEnabled && (
                                        <View style={styles.settingRow}>
                                            <View style={styles.settingInfo}>
                                                <Text style={styles.settingTitle}>Ticker Leagues & Sports</Text>
                                                <Text style={styles.settingDescription}>Select which leagues and sports appear in your game ticker. Use sport categories to quickly toggle all leagues in a sport.</Text>
                                            </View>
                                            <View style={[styles.settingValue, { alignItems: 'flex-start', paddingVertical: 12 }]}>
                                                <LinearGradient
                                                    colors={['rgba(255, 255, 255, 0.03)', 'rgba(255, 255, 255, 0.06)']}
                                                    start={{ x: 0, y: 0 }}
                                                    end={{ x: 0, y: 1 }}
                                                    style={styles.settingValueGradient}
                                                />
                                                
                                                {/* Sport Category Toggles */}
                                                <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: '600', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                                    Quick Select by Sport
                                                </Text>
                                                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 16, maxWidth: 500 }}>
                                                    {SPORT_CATEGORIES.map((category) => {
                                                        const sportLeagues = getLeaguesBySport(category.sport);
                                                        const selectedCount = sportLeagues.filter(l => tickerLeagues?.includes(l)).length;
                                                        const allSelected = selectedCount === sportLeagues.length;
                                                        const someSelected = selectedCount > 0 && !allSelected;
                                                        
                                                        return (
                                                            <TouchableOpacity
                                                                key={category.id}
                                                                onPress={() => {
                                                                    if (allSelected) {
                                                                        // Deselect all leagues in this sport
                                                                        const newLeagues = (tickerLeagues || []).filter(l => !sportLeagues.includes(l));
                                                                        updateTickerLeagues(newLeagues);
                                                                    } else {
                                                                        // Select all leagues in this sport
                                                                        const newLeagues = [...new Set([...(tickerLeagues || []), ...sportLeagues])];
                                                                        updateTickerLeagues(newLeagues);
                                                                    }
                                                                }}
                                                                style={{
                                                                    paddingVertical: 8,
                                                                    paddingHorizontal: 14,
                                                                    borderRadius: 8,
                                                                    borderWidth: 1,
                                                                    borderColor: allSelected ? '#8b5cf6' : someSelected ? '#f59e0b' : 'rgba(255,255,255,0.15)',
                                                                    backgroundColor: allSelected ? 'rgba(139, 92, 246, 0.2)' : someSelected ? 'rgba(245, 158, 11, 0.1)' : 'rgba(255,255,255,0.03)',
                                                                }}
                                                            >
                                                                <Text style={{
                                                                    color: allSelected ? '#a78bfa' : someSelected ? '#fbbf24' : 'rgba(255,255,255,0.6)',
                                                                    fontWeight: allSelected || someSelected ? '600' : '400',
                                                                    fontSize: 13,
                                                                }}>
                                                                    {category.label} {someSelected ? `(${selectedCount}/${sportLeagues.length})` : ''}
                                                                </Text>
                                                            </TouchableOpacity>
                                                        );
                                                    })}
                                                </View>
                                                
                                                {/* Individual League Toggles */}
                                                <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: '600', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                                    Individual Leagues
                                                </Text>
                                                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, maxWidth: 500 }}>
                                                    {AVAILABLE_TICKER_OPTIONS.map((option) => {
                                                        const isSelected = tickerLeagues?.includes(option.id);
                                                        return (
                                                            <TouchableOpacity
                                                                key={option.id}
                                                                onPress={() => {
                                                                    const newLeagues = isSelected
                                                                        ? tickerLeagues.filter(l => l !== option.id)
                                                                        : [...(tickerLeagues || []), option.id];
                                                                    updateTickerLeagues(newLeagues);
                                                                }}
                                                                style={{
                                                                    paddingVertical: 6,
                                                                    paddingHorizontal: 10,
                                                                    borderRadius: 16,
                                                                    borderWidth: 1,
                                                                    borderColor: isSelected ? '#4ade80' : 'rgba(255,255,255,0.12)',
                                                                    backgroundColor: isSelected ? 'rgba(74, 222, 128, 0.15)' : 'transparent',
                                                                }}
                                                            >
                                                                <Text style={{
                                                                    color: isSelected ? '#4ade80' : 'rgba(255,255,255,0.5)',
                                                                    fontWeight: isSelected ? '600' : '400',
                                                                    fontSize: 12,
                                                                }}>
                                                                    {option.label}
                                                                </Text>
                                                            </TouchableOpacity>
                                                        );
                                                    })}
                                                </View>
                                                
                                                {/* Quick Actions */}
                                                <View style={{ flexDirection: 'row', gap: 12, marginTop: 16, paddingTop: 12, borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.08)' }}>
                                                    <TouchableOpacity
                                                        onPress={() => updateTickerLeagues(DEFAULT_TICKER_LEAGUES)}
                                                        style={{ paddingVertical: 6, paddingHorizontal: 10 }}
                                                    >
                                                        <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, textDecorationLine: 'underline' }}>
                                                            Reset to Default
                                                        </Text>
                                                    </TouchableOpacity>
                                                    <TouchableOpacity
                                                        onPress={() => updateTickerLeagues(AVAILABLE_TICKER_OPTIONS.map(o => o.id))}
                                                        style={{ paddingVertical: 6, paddingHorizontal: 10 }}
                                                    >
                                                        <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, textDecorationLine: 'underline' }}>
                                                            Select All
                                                        </Text>
                                                    </TouchableOpacity>
                                                    <TouchableOpacity
                                                        onPress={() => updateTickerLeagues([])}
                                                        style={{ paddingVertical: 6, paddingHorizontal: 10 }}
                                                    >
                                                        <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, textDecorationLine: 'underline' }}>
                                                            Clear All
                                                        </Text>
                                                    </TouchableOpacity>
                                                </View>
                                            </View>
                                        </View>
                                    )}

                                    {/* Ticker Name Format */}
                                    {isTickerEnabled && (
                                        <View style={styles.settingRow}>
                                            <View style={styles.settingInfo}>
                                                <Text style={styles.settingTitle}>Ticker Name Format</Text>
                                                <Text style={styles.settingDescription}>Choose how team and player names are displayed in the ticker.</Text>
                                            </View>
                                            <View style={[styles.settingValue, { alignItems: 'flex-start', paddingVertical: 12 }]}>
                                                <LinearGradient
                                                    colors={['rgba(255, 255, 255, 0.03)', 'rgba(255, 255, 255, 0.06)']}
                                                    start={{ x: 0, y: 0 }}
                                                    end={{ x: 0, y: 1 }}
                                                    style={styles.settingValueGradient}
                                                />
                                                <View style={{ gap: 8, width: '100%', maxWidth: 400 }}>
                                                    {TICKER_NAME_FORMATS.map((format) => {
                                                        const isSelected = tickerNameFormat === format.id;
                                                        return (
                                                            <TouchableOpacity
                                                                key={format.id}
                                                                onPress={() => updateTickerNameFormat(format.id)}
                                                                style={{
                                                                    flexDirection: 'row',
                                                                    alignItems: 'center',
                                                                    padding: 12,
                                                                    borderRadius: 10,
                                                                    borderWidth: 1,
                                                                    borderColor: isSelected ? '#8b5cf6' : 'rgba(255,255,255,0.1)',
                                                                    backgroundColor: isSelected ? 'rgba(139, 92, 246, 0.15)' : 'rgba(255,255,255,0.02)',
                                                                }}
                                                            >
                                                                <View style={{
                                                                    width: 20,
                                                                    height: 20,
                                                                    borderRadius: 10,
                                                                    borderWidth: 2,
                                                                    borderColor: isSelected ? '#8b5cf6' : 'rgba(255,255,255,0.3)',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    marginRight: 12,
                                                                }}>
                                                                    {isSelected && (
                                                                        <View style={{
                                                                            width: 10,
                                                                            height: 10,
                                                                            borderRadius: 5,
                                                                            backgroundColor: '#8b5cf6',
                                                                        }} />
                                                                    )}
                                                                </View>
                                                                <View style={{ flex: 1 }}>
                                                                    <Text style={{
                                                                        color: isSelected ? '#a78bfa' : 'rgba(255,255,255,0.8)',
                                                                        fontWeight: isSelected ? '600' : '400',
                                                                        fontSize: 14,
                                                                    }}>
                                                                        {format.label}
                                                                    </Text>
                                                                    <Text style={{
                                                                        color: 'rgba(255,255,255,0.4)',
                                                                        fontSize: 12,
                                                                        marginTop: 2,
                                                                    }}>
                                                                        e.g., {format.example}
                                                                    </Text>
                                                                </View>
                                                            </TouchableOpacity>
                                                        );
                                                    })}
                                                </View>
                                            </View>
                                        </View>
                                    )}
                                    
                                    <View style={styles.settingRow}>
                                        <View style={styles.settingInfo}>
                                            <Text style={styles.settingTitle}>Default Sort Method</Text>
                                            <Text style={styles.settingDescription}>Choose how results are sorted by default. "Best EV%" sorts by expected value (edge over market). "Best Odds" sorts by most favorable betting odds.</Text>
                                        </View>
                                        <View style={styles.settingValue}>
                                            <LinearGradient
                                                colors={['rgba(255, 255, 255, 0.05)', 'rgba(255, 255, 255, 0.10)']}
                                                start={{ x: 0, y: 0 }}
                                                end={{ x: 0, y: 1 }}
                                                style={styles.settingValueGradient}
                                            />
                                            <View style={{ flexDirection: 'row', gap: 8 }}>
                                                <TouchableOpacity
                                                    onPress={() => setDefaultSortMethod('ev')}
                                                    style={[
                                                        { paddingVertical: 8, paddingHorizontal: 14, borderRadius: 8, borderWidth: 1, borderColor: 'rgba(255,255,255,0.18)' },
                                                        defaultSortMethod === 'ev' ? { backgroundColor: 'rgba(74, 222, 128, 0.3)', borderColor: '#4ade80' } : null
                                                    ]}
                                                >
                                                    <Text style={{ color: defaultSortMethod === 'ev' ? '#4ade80' : 'rgba(255,255,255,0.7)', fontWeight: defaultSortMethod === 'ev' ? '700' : '400', fontSize: 13 }}>
                                                        📊 Best EV%
                                                    </Text>
                                                </TouchableOpacity>
                                                <TouchableOpacity
                                                    onPress={() => setDefaultSortMethod('odds')}
                                                    style={[
                                                        { paddingVertical: 8, paddingHorizontal: 14, borderRadius: 8, borderWidth: 1, borderColor: 'rgba(255,255,255,0.18)' },
                                                        defaultSortMethod === 'odds' ? { backgroundColor: 'rgba(162, 89, 255, 0.3)', borderColor: '#A259FF' } : null
                                                    ]}
                                                >
                                                    <Text style={{ color: defaultSortMethod === 'odds' ? '#A259FF' : 'rgba(255,255,255,0.7)', fontWeight: defaultSortMethod === 'odds' ? '700' : '400', fontSize: 13 }}>
                                                        💰 Best Odds
                                                    </Text>
                                                </TouchableOpacity>
                                            </View>
                                        </View>
                                    </View>

                                    {/* Math Settings */}
                                    <Text style={styles.preferenceTitle}>Math Settings</Text>

                                    <View style={styles.settingRow}>
                                        <View style={styles.settingInfo}>
                                            <Text style={styles.settingTitle}>Kelly Criterion (Fractional)</Text>
                                            <Text style={styles.settingDescription}>Fraction of Full Kelly to bet. 0.25 = 1/4 Kelly (recommended), 0.5 = 1/2 Kelly, 1.0 = Full Kelly (aggressive).</Text>
                                        </View>
                                        <View style={styles.settingValue}>
                                            <LinearGradient
                                                colors={['rgba(255, 255, 255, 0.05)', 'rgba(255, 255, 255, 0.10)']}
                                                start={{ x: 0, y: 0 }}
                                                end={{ x: 0, y: 1 }}
                                                style={styles.settingValueGradient}
                                            />
                                            <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                                                {['0.125', '0.25', '0.5', '1.0'].map((val) => (
                                                    <TouchableOpacity
                                                        key={val}
                                                        onPress={() => setKellyCriterion(val)}
                                                        style={[
                                                            { paddingVertical: 6, paddingHorizontal: 10, borderRadius: 6, borderWidth: 1, borderColor: 'rgba(255,255,255,0.18)' },
                                                            kellyCriterion === val ? { backgroundColor: 'rgba(162, 89, 255, 0.4)', borderColor: '#A259FF' } : null
                                                        ]}
                                                    >
                                                        <Text style={{ color: kellyCriterion === val ? '#fff' : 'rgba(255,255,255,0.7)', fontWeight: kellyCriterion === val ? '700' : '400', fontSize: 13 }}>
                                                            {val === '0.125' ? '1/8' : val === '0.25' ? '1/4' : val === '0.5' ? '1/2' : 'Full'}
                                                        </Text>
                                                    </TouchableOpacity>
                                                ))}
                                                <TextInput
                                                    value={kellyCriterion}
                                                    onChangeText={setKellyCriterion}
                                                    placeholder="Custom"
                                                    placeholderTextColor="rgba(255,255,255,0.4)"
                                                    style={{
                                                        color: '#fff',
                                                        fontSize: 13,
                                                        paddingVertical: 6,
                                                        paddingHorizontal: 10,
                                                        borderRadius: 6,
                                                        borderWidth: 1,
                                                        borderColor: 'rgba(255,255,255,0.18)',
                                                        backgroundColor: 'rgba(0,0,0,0.2)',
                                                        minWidth: 70,
                                                        textAlign: 'center',
                                                        ...(Platform.OS === 'web' && { outlineStyle: 'none' })
                                                    }}
                                                />
                                            </View>
                                        </View>
                                    </View>

                                    <View style={styles.settingRow}>
                                        <View style={styles.settingInfo}>
                                            <Text style={styles.settingTitle}>Number of legs to display per page</Text>
                                            <Text style={styles.settingDescription}>Adjust the default number of legs/sgps to display per page without pagination.</Text>
                                        </View>
                                        <View style={styles.settingValue}>
                                            <LinearGradient
                                                colors={['rgba(255, 255, 255, 0.05)', 'rgba(255, 255, 255, 0.10)']}
                                                start={{ x: 0, y: 0 }}
                                                end={{ x: 0, y: 1 }}
                                                style={styles.settingValueGradient}
                                            />
                                            <View style={{ flexDirection: 'row', gap: 6 }}>
                                                {['5', '10', '20', '30'].map((val) => (
                                                    <TouchableOpacity
                                                        key={val}
                                                        onPress={() => setLegsPerPage(val)}
                                                        style={[
                                                            { paddingVertical: 6, paddingHorizontal: 10, borderRadius: 6, borderWidth: 1, borderColor: 'rgba(255,255,255,0.18)' },
                                                            legsPerPage === val ? { backgroundColor: 'rgba(255,255,255,0.2)' } : null
                                                        ]}
                                                    >
                                                        <Text style={{ color: legsPerPage === val ? '#fff' : 'rgba(255,255,255,0.7)', fontWeight: legsPerPage === val ? '700' : '400' }}>{val}</Text>
                                                    </TouchableOpacity>
                                                ))}
                                            </View>
                                        </View>
                                    </View>

                                    <View style={styles.settingRow}>
                                        <View style={styles.settingInfo}>
                                            <Text style={styles.settingTitle}>Minimum number of books needed for calcs (straights)</Text>
                                            <Text style={styles.settingDescription}>Adjust the default number of legs/sgps to display per page without pagination.</Text>
                                        </View>
                                        <SimpleInput
                                            value={minBooksStraights}
                                            onChangeText={setMinBooksStraights}
                                        />
                                    </View>

                                    <View style={styles.settingRow}>
                                        <View style={styles.settingInfo}>
                                            <Text style={styles.settingTitle}>Minimum number of books needed for calcs (SGP)</Text>
                                            <Text style={styles.settingDescription}>Adjust the default number of legs/sgps to display per page without pagination.</Text>
                                        </View>
                                        <SimpleInput
                                            value={minBooksSGP}
                                            onChangeText={setMinBooksSGP}
                                        />
                                    </View>

                                    <View style={styles.settingRow}>
                                        <View style={styles.settingInfo}>
                                            <Text style={styles.settingTitle}>Unit Size</Text>
                                            <Text style={styles.settingDescription}>Unit size by which incremental changes in bets will default from</Text>
                                        </View>
                                        <SimpleInput
                                            value={unitSize}
                                            onChangeText={setUnitSize}
                                        />
                                    </View>


                                    {/* Toggle Settings */}
                                    <Text style={styles.preferenceTitle}>Display Options</Text>
                                    
                                    <View style={styles.toggleRow}>
                                        <View style={styles.toggleInfo}>
                                            <Text style={styles.toggleTitle}>Show All Math by Default</Text>
                                            <Text style={styles.toggleDescription}>Always display detailed math calculations on bet cards without clicking to expand</Text>
                                        </View>
                                        <ToggleSwitch isOn={showAllMath} onToggle={() => setShowAllMath(!showAllMath)} />
                                    </View>
                                    
                                    <View style={styles.toggleRow}>
                                        <View style={styles.toggleInfo}>
                                            <Text style={styles.toggleTitle}>Auto-Switch Bet Mode</Text>
                                            <Text style={styles.toggleDescription}>Automatically switch between SGP/Straight/SGPx modes when you select incompatible legs. When disabled, the app will block selections instead of switching modes.</Text>
                                        </View>
                                        <ToggleSwitch isOn={autoSwitchMode} onToggle={() => setAutoSwitchMode(!autoSwitchMode)} />
                                    </View>
                                    
                                    <View style={[styles.toggleRow, { backgroundColor: 'rgba(139, 92, 246, 0.08)', borderRadius: 12, padding: 12, marginTop: 4 }]}>
                                        <View style={styles.toggleInfo}>
                                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                                                <Ionicons name="layers" size={18} color="#8b5cf6" />
                                                <Text style={[styles.toggleTitle, { color: '#c4b5fd' }]}>Background Queries by Default</Text>
                                            </View>
                                            <Text style={styles.toggleDescription}>
                                                When enabled, queries run in the background by default, allowing you to continue using the app while results load. 
                                                You can run up to 3 concurrent queries and view progress in the floating panel.
                                            </Text>
                                        </View>
                                        <ToggleSwitch isOn={backgroundQueryMode} onToggle={() => setBackgroundQueryMode(!backgroundQueryMode)} />
                                    </View>
                                    
                                    {/* Default Bet Mode Setting */}
                                    <View style={styles.settingRow}>
                                        <View style={styles.settingInfo}>
                                            <Text style={styles.settingTitle}>Default Bet Mode</Text>
                                            <Text style={styles.settingDescription}>Choose how the app determines bet type. Infer: automatically detects from query. SGP/Straight/SGPx: always use selected mode.</Text>
                                        </View>
                                        <View style={styles.settingValue}>
                                            <LinearGradient
                                                colors={['rgba(255, 255, 255, 0.05)', 'rgba(255, 255, 255, 0.10)']}
                                                start={{ x: 0, y: 0 }}
                                                end={{ x: 0, y: 1 }}
                                                style={styles.settingValueGradient}
                                            />
                                            <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
                                                {[
                                                    { value: 'infer', label: 'Infer' },
                                                    { value: 'sgp', label: 'SGP' },
                                                    { value: 'straight', label: 'Straight' },
                                                    { value: 'sgpx', label: 'SGPx' }
                                                ].map((mode) => (
                                                    <TouchableOpacity
                                                        key={mode.value}
                                                        onPress={() => setDefaultBetMode(mode.value)}
                                                        style={[
                                                            { paddingVertical: 6, paddingHorizontal: 10, borderRadius: 6, borderWidth: 1, borderColor: 'rgba(255,255,255,0.18)' },
                                                            defaultBetMode === mode.value ? { backgroundColor: 'rgba(162, 89, 255, 0.4)', borderColor: '#A259FF' } : null
                                                        ]}
                                                    >
                                                        <Text style={{ color: defaultBetMode === mode.value ? '#fff' : 'rgba(255,255,255,0.7)', fontWeight: defaultBetMode === mode.value ? '700' : '400', fontSize: 13 }}>
                                                            {mode.label}
                                                        </Text>
                                                    </TouchableOpacity>
                                                ))}
                                            </View>
                                        </View>
                                    </View>
                                </Animated.View>

                                {/* Action Buttons */}
                                <Animated.View style={[styles.actionButtons, { opacity: actionButtonsOpacity }]}>
                                    <TouchableOpacity
                                        style={[styles.saveButton, saving && styles.saveButtonDisabled]}
                                        onPress={handleSave}
                                        disabled={saving}
                                    >
                                        <Text style={styles.buttonText}>
                                            {saving ? 'Saving...' : 'Save Changes'}
                                        </Text>
                                    </TouchableOpacity>
                                    <TouchableOpacity style={styles.cancelButton}>
                                        <Text style={styles.buttonText}>Cancel</Text>
                                    </TouchableOpacity>
                                    <TouchableOpacity style={styles.logoutButton} onPress={handleLogout}>
                                        <Ionicons name="log-out-outline" size={16} color="rgba(255, 100, 100, 0.9)" />
                                        <Text style={styles.logoutButtonText}>Logout</Text>
                                    </TouchableOpacity>
                                </Animated.View>
                            </View>
                            )}
                        </ScrollView>
                    </BlurView>
                </Animated.View>
            </View>
        </View>
    );
};

const { width: screenWidth } = Dimensions.get('window');
const isMobile = screenWidth < 768;
const isTablet = screenWidth >= 768 && screenWidth < 1024;

// Responsive helper function
const responsive = (mobile, tablet, desktop) => {
  if (isMobile) return mobile;
  if (isTablet) return tablet;
  return desktop;
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#131313',
    },
    background: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#131313',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.25,
        shadowRadius: 4,
    },
    splashContainer: {
        flex: 1,
        width: '100%',
        height: '100%',
        paddingHorizontal: responsive(16, 32, Platform.OS === 'web' ? 48 : 24),
        paddingTop: responsive(12, 20, Platform.OS === 'web' ? 32 : 16),
        paddingBottom: responsive(16, 32, Platform.OS === 'web' ? 48 : 24),
        gap: responsive(24, 36, 48),
    },
    mainContent: {
        flex: 1,
        borderRadius: responsive(12, 16, 20),
        backgroundColor: '#000',
        padding: responsive(12, 18, 24),
        borderWidth: 1,
        borderBottomWidth: 2,
        borderTopColor: 'rgba(255,255,255,0.25)',
        borderLeftColor: 'rgba(255,255,255,0.18)',
        borderRightColor: 'rgba(255,255,255,0.12)',
        borderBottomColor: 'rgba(255,255,255,0.08)',
    },
    mainContentBlur: {
        flex: 1,
        borderRadius: responsive(12, 16, 20),
    },
    scrollView: {
        flex: 1,
    },
    settingsContainer: {
        width: '100%',
        paddingVertical: 24,
        backgroundColor: 'rgba(255, 255, 255, 0)',
        overflow: 'hidden',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        display: 'flex',
    },
    settingsTitle: {
        alignSelf: 'stretch',
        textAlign: 'center',
        color: 'rgba(255, 255, 255, 0.80)',
        fontSize: responsive(20, 24, 28),
        fontFamily: 'Slate',
        fontWeight: '500',
        lineHeight: responsive(24, 28, 32),
    },
    // Usage card styles
    usageCard: {
        alignSelf: 'stretch',
        marginTop: 20,
        marginBottom: 8,
        marginHorizontal: responsive(12, 20, 24),
        padding: responsive(16, 20, 24),
        borderRadius: 16,
        backgroundColor: 'rgba(139, 92, 246, 0.08)',
        borderWidth: 1,
        borderColor: 'rgba(139, 92, 246, 0.2)',
    },
    usageHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 16,
    },
    usageHeaderLeft: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    usageTitle: {
        color: '#e9e9e9',
        fontSize: 15,
        fontWeight: '600',
    },
    usagePlanBadge: {
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 12,
        backgroundColor: 'rgba(255,255,255,0.1)',
    },
    usagePlanBadgePro: {
        backgroundColor: 'rgba(139, 92, 246, 0.3)',
    },
    usagePlanBadgeUltimate: {
        backgroundColor: 'rgba(251, 191, 36, 0.3)',
    },
    usagePlanBadgeAdmin: {
        backgroundColor: 'rgba(74, 222, 128, 0.3)',
        borderWidth: 1,
        borderColor: 'rgba(74, 222, 128, 0.5)',
    },
    usagePlanText: {
        color: '#fff',
        fontSize: 11,
        fontWeight: '700',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    usageStats: {
        alignItems: 'center',
        marginBottom: 12,
    },
    usageCount: {
        fontSize: 28,
        fontWeight: '300',
        color: 'rgba(255,255,255,0.7)',
    },
    usageCountHighlight: {
        color: '#a78bfa',
        fontWeight: '700',
    },
    usageCountDivider: {
        color: 'rgba(255,255,255,0.4)',
    },
    usageLabel: {
        color: 'rgba(255,255,255,0.5)',
        fontSize: 13,
        marginTop: 2,
    },
    usageProgressContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
    },
    usageProgressBg: {
        flex: 1,
        height: 8,
        borderRadius: 4,
        backgroundColor: 'rgba(255,255,255,0.1)',
        overflow: 'hidden',
    },
    usageProgressFill: {
        height: '100%',
        borderRadius: 4,
    },
    usagePercentage: {
        color: 'rgba(255,255,255,0.6)',
        fontSize: 12,
        fontWeight: '600',
        minWidth: 36,
        textAlign: 'right',
    },
    usageFooter: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginTop: 12,
    },
    usageRemaining: {
        color: 'rgba(255,255,255,0.5)',
        fontSize: 12,
    },
    usageLimitReached: {
        color: '#f87171',
        fontSize: 12,
        fontWeight: '500',
    },
    usageUpgradeBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 8,
        backgroundColor: 'rgba(139, 92, 246, 0.15)',
        borderWidth: 1,
        borderColor: 'rgba(139, 92, 246, 0.3)',
    },
    usageUpgradeBtnText: {
        color: '#a78bfa',
        fontSize: 12,
        fontWeight: '600',
    },
    tabBtn: {
        paddingVertical: 8,
        paddingHorizontal: 12,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.14)'
    },
    tabBtnActive: {
        backgroundColor: 'rgba(255,255,255,0.10)',
        borderColor: 'rgba(255,255,255,0.28)'
    },
    tabBtnText: { color: 'rgba(255,255,255,0.8)', fontWeight: '600' },
    tabBtnTextActive: { color: '#fff' },
    profileSection: {
        width: '100%',
        maxWidth: 900,
        paddingVertical: 24,
        backgroundColor: 'rgba(255, 255, 255, 0)',
        justifyContent: 'flex-start',
        alignItems: 'flex-end',
        gap: 32,
        display: 'flex',
        flexDirection: 'row',
    },
    profileImageContainer: {
        width: 84,
        height: 84,
        position: 'relative',
        backgroundColor: 'rgba(0, 0, 0, 0)',
        boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25) inset',
        borderRadius: 999,
        // Add subtle border to indicate it's clickable
    },
    profileImageBackground: {
        width: 84,
        height: 84,
        position: 'absolute',
        left: 0,
        top: 0,
        borderRadius: 999,
        borderWidth: 3,
        overflow: 'hidden',
        borderColor: 'transparent',
    },
    profileImageGradient: {
        position: 'absolute',
        width: '100%',
        height: '100%',
    },
    profileImage: {
        width: '100%',
        height: '100%',
        position: 'absolute',
        objectFit: 'cover',
    },
    profileImagePlaceholder: {
        width: '100%',
        height: '100%',
        position: 'absolute',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        flexDirection: 'column',
        marginBottom: 5,
        gap: 2,
    },
    uploadText: {
        color: 'rgba(255, 255, 255, 0.60)',
        fontSize: 10,
        fontFamily: 'Slate',
        fontWeight: '400',
        lineHeight: 16,
        textAlign: 'center',
    },
    profileInfo: {
        flex: 1,
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: 4,
        display: 'flex',
    },
    profileHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
    },
    profileLabel: {
        color: 'rgba(255, 255, 255, 0.80)',
        fontSize: 16,
        fontFamily: 'Slate',
        fontWeight: '500',
        lineHeight: 16,
    },
    planCard: {
        flexDirection: 'row',
        gap: 10,
        paddingHorizontal: 12,
        paddingVertical: 10,
        minWidth: 120,
    },
    planText: {
        fontSize: 18,
        fontFamily: 'Slate',
        fontWeight: '500',
        color: 'rgba(255, 255, 255, 0.9)',
        textAlign: 'right',
    },
    profileInput: {
        alignSelf: 'stretch',
        borderRadius: 4,
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: 8,
        display: 'flex',
    },
    customInputContainer: {
        position: 'relative',
        width: '100%',
        height: '100%',
        borderRadius: 4,
        padding: 0,
        paddingVertical: 8,
        paddingHorizontal: 12,
        borderWidth: 1,
        borderTopColor: 'rgba(255,255,255,0.4)',
        borderLeftColor: 'rgba(255,255,255,0.3)',
        borderRightColor: 'rgba(0,0,0,0.3)',
        borderBottomColor: 'rgba(0,0,0,0.4)',
        overflow: 'hidden',
        // Embossed effect with shadows
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.3,
        shadowRadius: 2,
        elevation: 2,
        // Inner shadow for embossed look
        ...(Platform.OS === 'web' && {
            boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.2), inset 0 -1px 2px rgba(0,0,0,0.3), 0 1px 2px rgba(0,0,0,0.2)',
        }),
    },
    customInputBlur: {
        flex: 1,
        width: '100%',
        height: '100%',
        borderRadius: 4,
        justifyContent: 'center',
        alignItems: 'center',
    },
    customInputText: {
        flex: 1,
        color: '#B5B5B5',
        fontSize: 15,
        padding: 0,
        fontFamily: 'Slate',
        fontWeight: '400',
        // background shorthand removed for RNW
        backgroundColor: 'transparent',
        lineHeight: 20,
        width: '100%',
        ...(Platform.OS === 'web' ? { outlineStyle: 'none' } : {}),
    },
    section: {
        width: '100%',
        maxWidth: 900,
        paddingVertical: 24,
        backgroundColor: 'rgba(255, 255, 255, 0)',
        overflow: 'hidden',
        borderTopWidth: 2,
        borderTopColor: 'rgba(255, 255, 255, 0.80)',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: 16,
        display: 'flex',
    },
    sectionTitle: {
        alignSelf: 'stretch',
        color: 'rgba(255, 255, 255, 0.80)',
        fontSize: 20,
        fontFamily: 'Slate',
        fontWeight: '500',
        lineHeight: 24,
    },
    inputRow: {
        alignSelf: 'stretch',
        backgroundColor: 'rgba(255, 255, 255, 0)',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: 16,
        display: 'flex',
        flexDirection: 'row',
    },
    inputGroup: {
        flex: 1,
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: 4,
        display: 'flex',
    },
    inputLabel: {
        alignSelf: 'stretch',
        color: 'rgba(255, 255, 255, 0.80)',
        fontSize: 13,
        fontFamily: 'Slate',
        fontWeight: '500',
        lineHeight: 16,
    },
    inputContainer: {
        alignSelf: 'stretch',
        paddingHorizontal: 12,
        paddingVertical: 8,
        // gradient handled via overlays; remove shorthand
        borderRadius: 4,
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: 8,
        display: 'flex',
    },
    input: {
        flex: 1,
        color: '#B5B5B5',
        fontSize: 15,
        fontFamily: 'Slate',
        fontWeight: '400',
        lineHeight: 20,
    },
    preferenceItem: {
        alignSelf: 'stretch',
        paddingVertical: 16,
        backgroundColor: 'rgba(255, 255, 255, 0)',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: 16,
        display: 'flex',
    },
    preferenceTitle: {
        alignSelf: 'stretch',
        color: 'rgba(255, 255, 255, 0.80)',
        fontSize: 17,
        fontFamily: 'Slate',
        fontWeight: '400',
        lineHeight: 24,
    },
    preferenceDescription: {
        alignSelf: 'stretch',
        color: '#B5B5B5',
        fontSize: 15,
        fontFamily: 'Slate',
        fontWeight: '400',
        lineHeight: 20,
    },
    sportsbookFiltersContainer: {
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        display: 'flex',
        gap: 12,
    },
    sportsbookUnifiedGrid: {
        gap: 8,
        display: 'flex',
        flexDirection: 'column',
    },
    sportsbookUnifiedRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        paddingVertical: 8,
        paddingHorizontal: 12,
        borderRadius: 8,
        backgroundColor: 'rgba(255,255,255,0.03)',
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.08)',
    },
    sportsbookUnifiedCheckbox: {
        paddingRight: 8,
    },
    sportsbookUnifiedName: {
        color: 'rgba(255, 255, 255, 0.70)',
        fontSize: 14,
        fontFamily: 'Slate',
        fontWeight: '400',
        flex: 1,
    },
    sportsbookUnifiedLimitInput: {
        width: 80,
        textAlign: 'right',
    },
    customCheckbox: {
        width: 14,
        height: 14,
        backgroundColor: 'rgba(0, 0, 0, 0)',
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.3)',
        borderRadius: 2,
    },
    customCheckboxSelected: {
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        borderColor: 'rgba(255, 255, 255, 0.8)',
    },
    checkbox: {
        width: 14,
        height: 14,
        backgroundColor: 'rgba(0, 0, 0, 0)',
    },
    sportsbookName: {
        color: '#B5B5B5',
        fontSize: 15,
        fontFamily: 'Slate',
        fontWeight: '400',
        lineHeight: 20,
    },
    limitValue: {
        width: 50,
        padding: 10,
        backgroundColor: 'rgba(255, 255, 255, 0.10)',
        overflow: 'hidden',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 10,
        display: 'flex',
    },
    limitValueText: {
        color: 'white',
        fontSize: 15,
        fontFamily: 'Slate',
        fontWeight: '500',
        lineHeight: 20,
    },
    settingRow: {
        alignSelf: 'stretch',
        paddingVertical: 16,
        backgroundColor: 'rgba(255, 255, 255, 0)',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 16,
        display: 'flex',
        flexDirection: 'row',
    },
    settingInfo: {
        flex: 1,
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: 4,
        display: 'flex',
    },
    settingTitle: {
        alignSelf: 'stretch',
        color: 'rgba(255, 255, 255, 0.80)',
        fontSize: 17,
        fontFamily: 'Slate',
        fontWeight: '400',
        lineHeight: 24,
    },
    settingDescription: {
        alignSelf: 'stretch',
        color: '#B5B5B5',
        fontSize: 15,
        fontFamily: 'Slate',
        fontWeight: '400',
        lineHeight: 20,
    },
    settingValue: {
        minWidth: 120,
        width: 'auto',
        paddingVertical: 12,
        paddingHorizontal: 16,
        borderWidth: 1,
        borderBottomColor: 'rgba(255,255,255,0.2)',
        borderRightColor: 'rgba(255,255,255,0.1)',
        borderLeftColor: 'rgba(255,255,255,0.05)',
        borderTopColor: 'rgba(255,255,255,0.03)',
        borderRadius: 8,
        overflow: 'visible',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 10,
        display: 'flex',
        position: 'relative',
    },
    settingValueGradient: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        borderRadius: 4,
    },
    settingValueText: {
        color: 'white',
        fontSize: 15,
        fontFamily: 'Slate',
        fontWeight: '500',
        lineHeight: 20,
    },
    toggleRow: {
        alignSelf: 'stretch',
        paddingVertical: 16,
        backgroundColor: 'rgba(255, 255, 255, 0)',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: 16,
        display: 'flex',
        flexDirection: 'row',
    },
    toggleInfo: {
        flex: 1,
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: 4,
        display: 'flex',
    },
    toggleTitle: {
        alignSelf: 'stretch',
        color: 'rgba(255, 255, 255, 0.80)',
        fontSize: 17,
        fontFamily: 'Slate',
        fontWeight: '400',
        lineHeight: 24,
    },
    toggleDescription: {
        alignSelf: 'stretch',
        color: '#B5B5B5',
        fontSize: 15,
        fontFamily: 'Slate',
        fontWeight: '400',
        lineHeight: 20,
    },
    toggleSwitch: {
        width: 72,
        height: 36,
        position: 'relative',
        backgroundColor: 'rgba(255, 255, 255, 0.10)',
        overflow: 'hidden',
        borderRadius: 999,
        borderWidth: 1.5,
        borderBottomColor: 'rgba(255,255,255,0.2)',
        borderRightColor: 'rgba(255,255,255,0.1)',
        borderLeftColor: 'rgba(255,255,255,0.05)',
        borderTopColor: 'rgba(255,255,255,0.03)',
    },
    toggleSwitchBackground: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        borderRadius: 999,
    },
    toggleSwitchOn: {
        backgroundColor: 'rgba(255, 255, 255, 0.20)',
    },
    toggleThumb: {
        width: 28,
        height: 28,
        position: 'absolute',
        left: 4,
        top: 3,
        backgroundColor: 'rgba(255, 255, 255, 0.30)',
        borderRadius: 9999,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.3,
        shadowRadius: 2,
        transition: 'transform 0.2s ease-in-out',
        transitionProperty: 'transform',
        elevation: 2,
    },
    toggleThumbOn: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        shadowOpacity: 0.5,
        shadowRadius: 4,
        elevation: 4,
    },
    actionButtons: {
        width: '100%',
        maxWidth: 900,
        paddingVertical: 24,
        backgroundColor: 'rgba(255, 255, 255, 0)',
        overflow: 'hidden',
        borderTopWidth: 2,
        borderTopColor: 'rgba(255, 255, 255, 0.80)',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: 16,
        display: 'flex',
        flexDirection: 'row',
    },
    saveButton: {
        paddingHorizontal: 16,
        paddingVertical: 8,
        borderRadius: 4,
        borderWidth: 2,
        borderColor: 'rgba(255, 255, 255, 0.80)',
        justifyContent: 'center',
        alignItems: 'center',
        display: 'flex',
    },
    saveButtonDisabled: {
        opacity: 0.5,
        borderColor: 'rgba(255, 255, 255, 0.40)',
    },
    cancelButton: {
        paddingHorizontal: 16,
        paddingVertical: 8,
        borderRadius: 4,
        borderWidth: 2,
        borderColor: 'rgba(255, 255, 255, 0.80)',
        justifyContent: 'center',
        alignItems: 'center',
        display: 'flex',
    },
    logoutButton: {
        paddingHorizontal: 16,
        paddingVertical: 8,
        borderRadius: 4,
        borderWidth: 2,
        borderColor: 'rgba(255, 100, 100, 0.6)',
        backgroundColor: 'rgba(255, 100, 100, 0.1)',
        justifyContent: 'center',
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'row',
        gap: 8,
    },
    buttonText: {
        color: 'rgba(255, 255, 255, 0.80)',
        fontSize: 15,
        fontFamily: 'Slate',
        fontWeight: '500',
        lineHeight: 20,
    },
    logoutButtonText: {
        color: 'rgba(255, 100, 100, 0.9)',
        fontSize: 15,
        fontFamily: 'Slate',
        fontWeight: '500',
        lineHeight: 20,
    },
    manageSubscriptionButton: {
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 4,
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        justifyContent: 'center',
        alignItems: 'center',
    },
    manageSubscriptionButtonText: {
        color: 'rgba(255, 255, 255, 0.7)',
        fontSize: 14,
        fontFamily: 'Slate',
        fontWeight: '400',
        textAlign: 'center',
    },
    mathInput: {
        width: 120,
        height: 40,
    },
    limitInput: {
        width: 80,
        height: 32,
    },
    simpleInputContainer: {
        paddingHorizontal: 12,
        paddingVertical: 8,
        justifyContent: 'center',
    },
    simpleInputText: {
        color: '#B5B5B5',
        fontSize: 16,
        zIndex: 10,
        fontFamily: 'Slate',
        textAlign: 'center',
        fontWeight: '400',
        lineHeight: 22,
        minWidth: 100,
        width: 'auto',
        height: '100%',
        backgroundColor: 'transparent',
        ...(Platform.OS === 'web' && {
            outlineStyle: 'none',
        }),
    },
    // New grid layout styles for filters
    sportsbookFiltersGrid: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
        marginTop: 12,
    },
    sportsbookFilterCard: {
        flex: 1,
        minWidth: '22%',
        maxWidth: '24%',
        padding: 8,
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        borderRadius: 6,
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.1)',
        alignItems: 'center',
        gap: 4,
    },
    sportsbookFilterCheckbox: {
        alignSelf: 'center',
    },
    sportsbookFilterName: {
        color: 'rgba(255, 255, 255, 0.8)',
        fontSize: 12,
        fontFamily: 'Slate',
        fontWeight: '500',
        lineHeight: 14,
        textAlign: 'center',
    },
    sportsbookFilterLimitInput: {
        width: 50,
        height: 24,
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        borderRadius: 4,
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.2)',
        color: '#fff',
        fontSize: 10,
        textAlign: 'center',
        paddingHorizontal: 2,
    },
    showMoreButton: {
        marginTop: 16,
        paddingVertical: 8,
        paddingHorizontal: 16,
        backgroundColor: 'rgba(162, 89, 255, 0.2)',
        borderRadius: 6,
        borderWidth: 1,
        borderColor: 'rgba(162, 89, 255, 0.4)',
        alignSelf: 'center',
    },
    showMoreButtonText: {
        color: '#A259FF',
        fontSize: 14,
        fontFamily: 'Slate',
        fontWeight: '600',
        textAlign: 'center',
    },
    showLessButton: {
        marginTop: 16,
        paddingVertical: 8,
        paddingHorizontal: 16,
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        borderRadius: 6,
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.2)',
        alignSelf: 'center',
    },
    showLessButtonText: {
        color: 'rgba(255, 255, 255, 0.8)',
        fontSize: 14,
        fontFamily: 'Slate',
        fontWeight: '600',
        textAlign: 'center',
    },
    // Missing styles for filters
    preferenceItem: {
        marginBottom: 24,
    },
    sportsbookFiltersContainer: {
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        borderRadius: 8,
        padding: 16,
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.1)',
    },
    preferenceTitle: {
        color: 'rgba(255, 255, 255, 0.9)',
        fontSize: 18,
        fontFamily: 'Slate',
        fontWeight: '600',
        marginBottom: 8,
    },
    preferenceDescription: {
        color: 'rgba(255, 255, 255, 0.7)',
        fontSize: 14,
        fontFamily: 'Slate',
        fontWeight: '400',
        lineHeight: 20,
        marginBottom: 16,
    },
    tag: {
        paddingVertical: 6,
        paddingHorizontal: 12,
        borderRadius: 6,
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.2)',
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
    },
    tagActive: {
        backgroundColor: 'rgba(162, 89, 255, 0.3)',
        borderColor: '#A259FF',
    },
    tagText: {
        color: 'rgba(255, 255, 255, 0.7)',
        fontSize: 13,
        fontFamily: 'Slate',
        fontWeight: '500',
    },
    tagTextActive: {
        color: '#fff',
        fontWeight: '600',
    },
    // Query history metric chips
    metricChip: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        paddingVertical: 4,
        paddingHorizontal: 8,
        backgroundColor: 'rgba(139, 92, 246, 0.15)',
        borderRadius: 6,
        borderWidth: 1,
        borderColor: 'rgba(139, 92, 246, 0.3)',
    },
    metricChipText: {
        color: '#c4b5fd',
        fontSize: 11,
        fontWeight: '600',
    },
});

export default ProfilePage;
